import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowRightIcon,
  BanknotesIcon,
  ClockIcon,
  CreditCardIcon,
  InformationCircleIcon,
  PaintBrushIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { EditorState, Modifier, ContentState } from 'draft-js';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { getCountryOptions } from '@charitystack/shared';
import {
  DateConfigType,
  FrequencyAmountsMap,
  FrequencyScheduledRangeMap,
  RecurringFrequency,
  ScheduledRangeConfig,
} from '../CreateFundraiserDrawer.types';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import { FormInput } from '../../../components/ui/form-input';
import { FormTextarea } from '../../../components/ui/form-textarea';
import { FormColorSelector } from '../../../components/ui/form-color-selector';
import { FormSelect } from '../../../components/ui/form-select';
import { SectionHeader } from '@/components/ui/section-header';
import { WorkspaceSectionCard } from '../../../../components/workspaces/EntityWorkspacePrimitives';
import { COLORS } from '../../../constants/colors';
import ImageUploader from '../../uploads/ImageUploader';
import StyledMultiSelectDropdown from '../../dropdowns/StyledMultiSelectDropdown';
import MultiSelectDropdownNew from '../../dropdowns/MultiSelectDropdownNew';
import { Separator } from '@/components/ui/separator';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import Checkbox from '../../../components/ui/checkbox';
// @ts-expect-error - SingleDatePicker is a JSX file without type definitions
import SingleDatePicker from '../../../components/ui/SingleDatePicker';
import { getValidationToastMessage } from '../../../utils/validationToast';
import { formatWithCommas, stripNonNumeric } from '../../../utils/formatters';

const FREQ_TAB_BASE =
  'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 outline-none ring-0 focus:outline-none focus:ring-0';
const FREQ_TAB_ACTIVE = 'border-ink-secondary text-ink-secondary';
const FREQ_TAB_INACTIVE = 'border-transparent text-ink-muted hover:text-ink-secondary';
const FREQ_TAB_ERROR_ACTIVE = 'border-red-500 text-red-600';
const FREQ_TAB_ERROR_INACTIVE = 'border-transparent text-red-500 hover:text-[#A04040]';

const getFreqTabClasses = (isActive: boolean, hasError: boolean): string =>
  `${FREQ_TAB_BASE} ${
    isActive
      ? hasError
        ? FREQ_TAB_ERROR_ACTIVE
        : FREQ_TAB_ACTIVE
      : hasError
        ? FREQ_TAB_ERROR_INACTIVE
        : FREQ_TAB_INACTIVE
  }`;

// Form steps for stepper
const FORM_STEPS = {
  APPEARANCE: 'appearance',
  FREQUENCY: 'frequency',
  AMOUNTS: 'amounts',
  CHECKOUT: 'checkout',
};

// Type for selectable fund items
type SelectableFund =
  | {
      value?: string;
      label?: string;
    }
  | string;

// ButtonGroup Component
interface ButtonGroupProps {
  currentStep: string;
  onBack: () => void;
  onContinue: () => void;
  isLastStep: boolean;
  validateCurrentStep: () => boolean;
}

const ButtonGroup = ({
  currentStep,
  onBack,
  onContinue,
  isLastStep,
  validateCurrentStep,
}: ButtonGroupProps) => {
  const getButtonText = () => {
    return 'Continue';
  };

  const handleContinue = () => {
    if (!validateCurrentStep()) {
      return;
    }
    onContinue();
  };

  return (
    <div className="flex w-full justify-between">
      {/* Back Button - Show on all steps except first */}
      <div>
        {currentStep !== FORM_STEPS.APPEARANCE && (
          <Button onClick={onBack} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <ArrowLeftIcon className="h-[14px] w-[14px] text-ink-muted" />
            <div>Back</div>
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Continue Button - No special handling for last step */}
        {!isLastStep && (
          <Button onClick={handleContinue} className="h-[28px] gap-x-1 px-3 text-sm text-ink-muted">
            <div>{getButtonText()}</div>
            <ArrowRightIcon className="h-[14px] w-[14px] text-ink-muted" />
          </Button>
        )}
      </div>
    </div>
  );
};

// Types for Crowdfunding Options (Giving Levels)
interface CrowdfundingOption {
  id: string;
  title: string;
  amount: string;
  description: string;
}

// Types for Sponsorships (Products)
interface ProductOption {
  productID: string;
  productName: string;
  productPrice: string;
}

interface ProductGroup {
  id: string;
  productGroupName: string;
  productGroupDescription: string;
  products: ProductOption[];
}

interface FundraiserFormConfigurationTabProps {
  workspacePresentation?: boolean;
  // Current step state
  currentStep: 'appearance' | 'frequency' | 'amounts' | 'checkout';
  setCurrentStep: (step: 'appearance' | 'frequency' | 'amounts' | 'checkout') => void;

  // Navigation functions
  handleContinue: () => void;
  handleBack: () => void;

  // Validation props
  setInvalidFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleFieldFocus: (fieldName: string) => void;
  isFieldInvalid: (fieldName: string) => boolean;
  invalidFields: Set<string>;

  // Appearance step props
  fundraiserName: string;
  setFundraiserName: (value: string) => void;
  color: string;
  setColor: (value: string) => void;
  targetAmount: string;
  setTargetAmount: (value: string) => void;
  enableDonorList: boolean;
  donorListType: string;
  setDonorListType: (value: string) => void;
  setEnableDonorList: (value: boolean) => void;
  showAllSupportersAnonymous?: boolean;
  setShowAllSupportersAnonymous?: (value: boolean) => void;
  showFundraisingBar: boolean;
  setShowFundraisingBar: (value: boolean) => void;
  amountOffset: string;
  setAmountOffset: (value: string) => void;
  trackingMetric: string;
  setTrackingMetric: (value: string) => void;
  metricLabel: string;
  setMetricLabel: (value: string) => void;
  countOffset: string;
  setCountOffset: (value: string) => void;
  merchantName?: string;
  elementID?: string;
  image?: string | null;
  setImage: (image: string | null) => void;
  setResizedImage: (image: string | null) => void;

  // Description props
  description: string;
  setDescription: (description: string) => void;

  // Fund selection props
  funds: string[];
  setFunds: (value: string[]) => void;
  selectableFunds?: SelectableFund[];

  // Frequency step props
  checkedValues: string[];
  setCheckedValues: (values: string[]) => void;
  defaultDonationType: string;
  setDefaultDonationType: (value: string) => void;
  requireScheduleDonations: boolean;
  setRequireScheduleDonations: (value: boolean) => void;
  // Per-frequency scheduled payment configuration
  frequencyScheduledRanges?: FrequencyScheduledRangeMap;
  setFrequencyScheduledRanges?: (value: FrequencyScheduledRangeMap) => void;
  activeScheduleTab?: string;
  setActiveScheduleTab?: (value: string) => void;

  // Amount step props
  frequencyAmounts?: FrequencyAmountsMap;
  setFrequencyAmounts?: (value: FrequencyAmountsMap) => void;

  // Crowdfunding Options props (Giving Levels)
  crowdfundingOptions?: CrowdfundingOption[];
  setCrowdfundingOptions?: (value: CrowdfundingOption[]) => void;

  // Per-frequency Crowdfunding Options (NEW - mirrors Standard form pattern)
  oneTimeCrowdfundingOptions?: CrowdfundingOption[];
  setOneTimeCrowdfundingOptions?: (value: CrowdfundingOption[]) => void;
  dailyCrowdfundingOptions?: CrowdfundingOption[];
  setDailyCrowdfundingOptions?: (value: CrowdfundingOption[]) => void;
  weeklyCrowdfundingOptions?: CrowdfundingOption[];
  setWeeklyCrowdfundingOptions?: (value: CrowdfundingOption[]) => void;
  monthlyCrowdfundingOptions?: CrowdfundingOption[];
  setMonthlyCrowdfundingOptions?: (value: CrowdfundingOption[]) => void;
  annuallyCrowdfundingOptions?: CrowdfundingOption[];
  setAnnuallyCrowdfundingOptions?: (value: CrowdfundingOption[]) => void;

  // Sponsorships props (Product Groups)
  productGroups?: ProductGroup[];
  setProductGroups?: (value: ProductGroup[]) => void;

  // Per-frequency Product Groups (NEW - mirrors Standard form pattern)
  oneTimeProductGroups?: ProductGroup[];
  setOneTimeProductGroups?: (value: ProductGroup[]) => void;
  dailyProductGroups?: ProductGroup[];
  setDailyProductGroups?: (value: ProductGroup[]) => void;
  weeklyProductGroups?: ProductGroup[];
  setWeeklyProductGroups?: (value: ProductGroup[]) => void;
  monthlyProductGroups?: ProductGroup[];
  setMonthlyProductGroups?: (value: ProductGroup[]) => void;
  annuallyProductGroups?: ProductGroup[];
  setAnnuallyProductGroups?: (value: ProductGroup[]) => void;

  // Checkout step props
  showAnonymousDonations: boolean;
  setShowAnonymousDonations: (value: boolean) => void;
  showOrganizationNameField: boolean;
  setShowOrganizationNameField: (value: boolean) => void;
  enablePhoneNumber: boolean;
  setEnablePhoneNumber: (value: boolean) => void;
  enableBillingAddress: boolean;
  setEnableBillingAddress: (value: boolean) => void;
  enableDoubleTheDonation: boolean;
  setEnableDoubleTheDonation: (value: boolean) => void;
  selectedCountry: string;
  setSelectedCountry: (value: string) => void;

  // Communication Consent props
  enableCommunicationConsent: boolean;
  setEnableCommunicationConsent: (value: boolean) => void;
  communicationConsentMessage: string;
  setCommunicationConsentMessage: (value: string) => void;

  // Dedication props
  enableDedication: boolean;
  setEnableDedication: (value: boolean) => void;
  dedicationTypes: string[];
  setDedicationTypes: (value: string[]) => void;
  enableDedicationMessage: boolean;
  setEnableDedicationMessage: (value: boolean) => void;

  // Cover fees lock props
  lockCoverFees?: boolean;
  setLockCoverFees?: (value: boolean) => void;

  // Checkout bump opt-out props
  disableCheckoutBump?: boolean;
  setDisableCheckoutBump?: (value: boolean) => void;

  // Rich text editor props
  editorState: EditorState;
  setEditorState: (editorState: EditorState) => void;

  // Elements props
  enableEmbedForm: boolean;
  setEnableEmbedForm: (value: boolean) => void;

  // Form type props
  formType: string;
  setFormType: (value: string) => void;
  fundraiserType: string;
  setFundraiserType: (value: string) => void;

  showFieldMappingModal: boolean;
  setShowFieldMappingModal: (value: boolean) => void;

  integrationType: string | null;
  fieldMappings: any[];
  setFieldMappings: (value: any[]) => void;

  // Salesforce Campaign ID props (ICNA Relief only)
  salesforceCampaignIDICNAValues?: string;
  setSalesforceCampaignIDICNAValues?: (value: string) => void;
  setSalesforceFieldMapperOpen?: (value: boolean) => void;

  // Feature flags
  dtdPublicApiKey?: string | null;

  // Edit mode flag — when true, sponsorship group limits are relaxed
  isEditMode?: boolean;
}

export default function FundraiserFormConfigurationTab(props: FundraiserFormConfigurationTabProps) {
  const workspacePresentation = props.workspacePresentation === true;
  const {
    currentStep,
    setCurrentStep,
    handleContinue,
    handleBack,

    // Validation props
    setInvalidFields,
    handleFieldFocus,
    isFieldInvalid,
    invalidFields,

    // Appearance props
    fundraiserName,
    setFundraiserName,
    color,
    setColor,
    targetAmount,
    setTargetAmount,
    enableDonorList,
    setEnableDonorList,
    donorListType,
    setDonorListType,
    showAllSupportersAnonymous = false,
    setShowAllSupportersAnonymous,
    showFundraisingBar,
    setShowFundraisingBar,
    amountOffset,
    setAmountOffset,
    trackingMetric,
    setTrackingMetric,
    metricLabel,
    setMetricLabel,
    countOffset,
    setCountOffset,
    merchantName,
    elementID,
    image,
    setImage,
    setResizedImage,

    // Description props
    description,
    setDescription,

    // Fund selection props
    funds,
    setFunds,
    selectableFunds,

    // Frequency props
    checkedValues,
    setCheckedValues,
    defaultDonationType,
    setDefaultDonationType,
    requireScheduleDonations,
    setRequireScheduleDonations,
    frequencyScheduledRanges,
    setFrequencyScheduledRanges,
    activeScheduleTab,
    setActiveScheduleTab,

    // Amount props
    frequencyAmounts,
    setFrequencyAmounts,

    // Crowdfunding Options props
    crowdfundingOptions,
    setCrowdfundingOptions,

    // Per-frequency Crowdfunding Options (NEW)
    oneTimeCrowdfundingOptions,
    setOneTimeCrowdfundingOptions,
    dailyCrowdfundingOptions,
    setDailyCrowdfundingOptions,
    weeklyCrowdfundingOptions,
    setWeeklyCrowdfundingOptions,
    monthlyCrowdfundingOptions,
    setMonthlyCrowdfundingOptions,
    annuallyCrowdfundingOptions,
    setAnnuallyCrowdfundingOptions,

    // Sponsorships props
    productGroups,
    setProductGroups,

    // Per-frequency Product Groups (NEW)
    oneTimeProductGroups,
    setOneTimeProductGroups,
    dailyProductGroups,
    setDailyProductGroups,
    weeklyProductGroups,
    setWeeklyProductGroups,
    monthlyProductGroups,
    setMonthlyProductGroups,
    annuallyProductGroups,
    setAnnuallyProductGroups,

    // Checkout props
    selectedCountry,
    setSelectedCountry,

    // Rich text editor props
    editorState,
    setEditorState,

    enableEmbedForm,
    setEnableEmbedForm,
    showAnonymousDonations,
    setShowAnonymousDonations,
    showOrganizationNameField,
    setShowOrganizationNameField,
    enablePhoneNumber,
    setEnablePhoneNumber,
    enableBillingAddress,
    setEnableBillingAddress,
    enableDoubleTheDonation,
    setEnableDoubleTheDonation,
    enableCommunicationConsent,
    setEnableCommunicationConsent,
    communicationConsentMessage,
    setCommunicationConsentMessage,
    enableDedication,
    setEnableDedication,
    dedicationTypes,
    setDedicationTypes,
    enableDedicationMessage,
    setEnableDedicationMessage,
    lockCoverFees = false,
    setLockCoverFees,
    disableCheckoutBump = false,
    setDisableCheckoutBump,
    formType,
    setFormType,
    fundraiserType,
    setFundraiserType,
    showFieldMappingModal,
    setShowFieldMappingModal,
    integrationType,
    fieldMappings,
    setFieldMappings,
    salesforceCampaignIDICNAValues,
    setSalesforceCampaignIDICNAValues,
    setSalesforceFieldMapperOpen,

    // Feature flags (defaults to true for backward compatibility)
    dtdPublicApiKey = null,

    // Edit mode — when true, sponsorship group limit is not enforced
    isEditMode = false,
  } = props;

  // Handler to strip HTML formatting when pasting into the editor
  const handlePastedText = (text: string, html: string, editorState: EditorState): boolean => {
    // Use DOMParser to properly strip HTML tags and decode entities
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const plainText = doc.body.textContent || '';

    // Insert the plain text into the editor
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const newContent = Modifier.replaceText(currentContent, selection, plainText);
    const newEditorState = EditorState.push(editorState, newContent, 'insert-characters');

    setEditorState(newEditorState);
    return true; // Return true to indicate the paste was handled
  };

  // Local states for various UI elements
  const [activeAmountTab, setActiveAmountTab] = useState(checkedValues[0] || '');
  const [activeAmountTabIndex, setActiveAmountTabIndex] = useState(0);
  const [activeCrowdfundingTab, setActiveCrowdfundingTab] = useState(
    checkedValues[0] || 'One-Time',
  );
  const [activeSponsorshipTab, setActiveSponsorshipTab] = useState(checkedValues[0] || 'One-Time');
  const [givingLevelsLimitReached, setGivingLevelsLimitReached] = useState(false);
  const [sponsorshipGroupsLimitReached, setSponsorshipGroupsLimitReached] = useState(false);

  const frequencyOptions = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
  const frequencyOrder = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
  const countries = useMemo(() => getCountryOptions(), []); // frozen canonical options (PE-1878)
  // PE-1878 FE window: a stored country value ("Israel"/"Palestine, State of")
  // may no longer match a canonical option before the data backfill runs. Keep
  // any orphan value selectable so an existing form doesn't silently go blank
  // (and a re-save doesn't drop the country). Mirrors CreateEditContactDrawer.
  const countryLabels = useMemo(
    () => new Set(countries.map((c: { value: string; label: string }) => c.label)),
    [countries],
  );

  useEffect(() => {
    if (checkedValues.length > 0) {
      setActiveAmountTab(checkedValues[0]);
    }
  }, [checkedValues]);

  // Ensure activeCrowdfundingTab is always set to first enabled frequency
  useEffect(() => {
    if (checkedValues.length > 0 && formType === 'CROWDFUNDING_FORM') {
      // Only update if current tab is not in checkedValues (invalid state)
      if (!checkedValues.includes(activeCrowdfundingTab)) {
        setActiveCrowdfundingTab(checkedValues[0]);
      }
    }
  }, [checkedValues, formType, activeCrowdfundingTab]);

  // Ensure activeSponsorshipTab is always set to first enabled frequency
  useEffect(() => {
    if (checkedValues.length > 0 && formType === 'PRODUCT') {
      // Only update if current tab is not in checkedValues (invalid state)
      if (!checkedValues.includes(activeSponsorshipTab)) {
        setActiveSponsorshipTab(checkedValues[0]);
      }
    }
  }, [checkedValues, formType, activeSponsorshipTab]);

  useEffect(() => {
    if (formType === 'EMBED_FORM' || formType === 'CROWDFUNDING_FORM') {
      setFundraiserType('DONATION_FORM');
    } else if (formType === 'PRODUCT') {
      setFundraiserType('PRODUCT');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType]); // Only depend on formType, not the setter function

  // Track previous checkedValues to detect when frequencies change
  const prevCheckedValuesRef = React.useRef<string[]>(checkedValues);

  // Set activeScheduleTab to first recurring frequency when frequencies change
  useEffect(() => {
    if (requireScheduleDonations && setActiveScheduleTab) {
      const recurringFrequencies = checkedValues.filter((freq) => freq !== 'One-Time');
      const prevRecurringFrequencies = prevCheckedValuesRef.current.filter(
        (freq) => freq !== 'One-Time',
      );

      // Check if frequencies actually changed (not just a tab click)
      const frequenciesChanged =
        recurringFrequencies.length !== prevRecurringFrequencies.length ||
        recurringFrequencies.some((freq) => !prevRecurringFrequencies.includes(freq)) ||
        prevRecurringFrequencies.some((freq) => !recurringFrequencies.includes(freq));

      // Always reset to first tab when frequencies change, or if current tab is invalid
      if (
        frequenciesChanged ||
        !activeScheduleTab ||
        !recurringFrequencies.includes(activeScheduleTab)
      ) {
        const frequencyOrder: RecurringFrequency[] = ['Daily', 'Weekly', 'Monthly', 'Annually'];
        for (const freq of frequencyOrder) {
          if (recurringFrequencies.includes(freq)) {
            setActiveScheduleTab(freq);
            break;
          }
        }
      }

      // Update ref for next comparison
      prevCheckedValuesRef.current = checkedValues;
    }
  }, [requireScheduleDonations, checkedValues, activeScheduleTab, setActiveScheduleTab]);

  // Generate time options in hourly intervals (00:00 to 23:00)
  const getTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      const timeString = `${hour.toString().padStart(2, '0')}:00`;
      options.push(timeString);
    }
    return options;
  };

  // Format a 24h "HH:00" value to a 12h AM/PM display label (e.g. "14:00" → "2:00 PM").
  // Display only — the stored value stays in 24h "HH:00" (resolver/backend expect that).
  // Mirrors formatHourDisplay in donation-elements ControlledRecurringDatePicker.
  const formatHourDisplay = (timeValue: string): string => {
    const hour = parseInt(timeValue.split(':')[0], 10);
    if (Number.isNaN(hour)) return timeValue;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  // Map frequency tab to schedule type (time unit)
  const getScheduleTypeForFrequency = (freq: RecurringFrequency): string => {
    switch (freq) {
      case 'Daily':
        return 'days';
      case 'Weekly':
        return 'weeks';
      case 'Monthly':
        return 'months';
      case 'Annually':
        return 'years';
      default:
        return 'days';
    }
  };

  // Helper to clear schedule validation errors for a specific frequency
  // Called when user modifies schedule config to provide immediate feedback
  const clearScheduleValidationErrors = (freq: RecurringFrequency) => {
    setInvalidFields((prevInvalidFields) => {
      const newInvalidFields = new Set(prevInvalidFields);
      newInvalidFields.delete(`form_configuration-frequency-${freq}-startDate`);
      newInvalidFields.delete(`form_configuration-frequency-${freq}-endDate`);
      newInvalidFields.delete(`form_configuration-frequency-${freq}-scheduleOffsetValidation`);
      return newInvalidFields;
    });
  };

  // Helper to clear validation errors for a specific frequency (Giving Levels)
  // Called when auto-copying data from one frequency to another to prevent false validation errors
  const clearValidationErrorsForFrequency = (freq: string) => {
    setInvalidFields((prevInvalidFields) => {
      const newInvalidFields = new Set(prevInvalidFields);

      // Remove all validation errors for this frequency
      // Pattern: form_configuration-amounts-crowdfunding-{freq}-{index}-{field}
      // Examples:
      //   - form_configuration-amounts-crowdfunding-Monthly-0-amount
      //   - form_configuration-amounts-crowdfunding-Monthly-0-title
      //   - form_configuration-amounts-crowdfunding-Monthly-0-description
      for (const field of newInvalidFields) {
        if (field.startsWith(`form_configuration-amounts-crowdfunding-${freq}-`)) {
          newInvalidFields.delete(field);
        }
      }

      return newInvalidFields;
    });
  };

  // Helper to clear validation errors for a specific frequency (Sponsorships)
  // Called when auto-copying data from one frequency to another to prevent false validation errors
  const clearSponsorshipValidationErrorsForFrequency = (freq: string) => {
    setInvalidFields((prevInvalidFields) => {
      const newInvalidFields = new Set(prevInvalidFields);

      // Remove all validation errors for this frequency
      // Pattern: form_configuration-amounts-sponsorship-{freq}-{groupIndex}-{field}
      // Examples:
      //   - form_configuration-amounts-sponsorship-Monthly-0-name
      //   - form_configuration-amounts-sponsorship-Monthly-0-products
      //   - form_configuration-amounts-sponsorship-Monthly-0-product-0-name
      //   - form_configuration-amounts-sponsorship-Monthly-0-product-0-price
      for (const field of newInvalidFields) {
        if (field.startsWith(`form_configuration-amounts-sponsorship-${freq}-`)) {
          newInvalidFields.delete(field);
        }
      }

      return newInvalidFields;
    });
  };

  // Helper to update schedule config for a specific frequency (single field)
  // Automatically sets scheduleType based on frequency
  const updateScheduleConfigForFrequency = (
    freq: RecurringFrequency,
    field: keyof ScheduledRangeConfig,
    value: any,
  ) => {
    if (!setFrequencyScheduledRanges || !frequencyScheduledRanges) return;

    const currentConfig = frequencyScheduledRanges[freq] || {
      enabled: false,
      scheduleType: getScheduleTypeForFrequency(freq),
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    };

    setFrequencyScheduledRanges({
      ...frequencyScheduledRanges,
      [freq]: {
        ...currentConfig,
        enabled: true,
        scheduleType: getScheduleTypeForFrequency(freq), // Always set scheduleType based on frequency
        [field]: value,
      },
    });

    // Clear validation errors when user modifies schedule config
    clearScheduleValidationErrors(freq);
  };

  // Helper to update multiple fields at once (prevents race conditions)
  // Automatically sets scheduleType based on frequency
  const updateScheduleConfigMultipleFields = (
    freq: RecurringFrequency,
    updates: Partial<ScheduledRangeConfig>,
  ) => {
    if (!setFrequencyScheduledRanges || !frequencyScheduledRanges) return;

    const currentConfig = frequencyScheduledRanges[freq] || {
      enabled: false,
      scheduleType: getScheduleTypeForFrequency(freq),
      startDateOffset: null,
      endDateOffset: null,
      scheduledTime: '',
    };

    setFrequencyScheduledRanges({
      ...frequencyScheduledRanges,
      [freq]: {
        ...currentConfig,
        enabled: true,
        scheduleType: getScheduleTypeForFrequency(freq), // Always set scheduleType based on frequency
        ...updates,
      },
    });

    // Clear validation errors when user modifies schedule config
    clearScheduleValidationErrors(freq);
  };

  // Helper to format time unit label (singular/plural) based on frequency
  const formatTimeUnit = (freq: RecurringFrequency, num: number) => {
    const scheduleType = getScheduleTypeForFrequency(freq);
    return num === 1 ? scheduleType.slice(0, -1) : scheduleType;
  };

  // ─── Fixed recurring charge day (fixedChargeDay) helpers ───
  // The "Charge on a fixed day" section lets an org pin the recurring day donors
  // are charged on. Frequency-interpreted; DAILY has no day anchor (time-only).
  const WEEKDAY_LABELS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const MONTH_LABELS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Ordinal suffix for month days (1st, 2nd, 3rd, …, 31st (last day)).
  const ordinalDayLabel = (day: number): string => {
    if (day === 31) return '31st (last day)';
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return `${day}st`;
    if (j === 2 && k !== 12) return `${day}nd`;
    if (j === 3 && k !== 13) return `${day}rd`;
    return `${day}th`;
  };

  const getFixedChargeDay = (freq: RecurringFrequency): string | number | null | undefined =>
    frequencyScheduledRanges?.[freq]?.fixedChargeDay;

  // Days in a given 1-based month for the ANNUALLY day picker. Uses a leap year
  // so February allows 29 — the anchor recurs yearly and may legitimately target
  // Feb 29 (the resolver seeds the next leap year).
  const daysInAnnualMonth = (month: number): number => {
    if (month === 2) return 29;
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
  };

  // Whether the per-frequency "fixed day" section is on. Derived from data: a
  // fixed charge day exists for this frequency. No separate persisted toggle.
  const isFixedChargeDayEnabled = (freq: RecurringFrequency): boolean => {
    const value = getFixedChargeDay(freq);
    return value !== null && value !== undefined && value !== '';
  };

  // Whether the per-frequency "start/end window" section is on. Derived from
  // data and defaults ON when first opened: a brand-new config (no startDateType/
  // endDateType yet) shows the window expanded. It only reads as OFF once the user
  // explicitly turns it off (both date types set to 'none', which toggleScheduleWindow
  // writes). This keeps the derive-from-data model while honoring "default on".
  const isWindowEnabled = (freq: RecurringFrequency): boolean => {
    const config = frequencyScheduledRanges?.[freq];
    if (!config) return true; // unconfigured → default on
    const startType = config.startDateType;
    const endType = config.endDateType;
    // Explicit off: both types present and set to 'none'.
    if (startType === 'none' && endType === 'none') return false;
    return true;
  };

  // Whether the start/end window has an ACTUAL configured value (a date type
  // actively chosen, not 'none'/unset). Distinct from isWindowEnabled, which
  // defaults on for an unconfigured frequency — an on-but-empty window is not
  // yet "configured", so it should not surface the Lock control.
  const hasWindowValue = (freq: RecurringFrequency): boolean => {
    const config = frequencyScheduledRanges?.[freq];
    if (!config) return false;
    const startSet = config.startDateType && config.startDateType !== 'none';
    const endSet = config.endDateType && config.endDateType !== 'none';
    return Boolean(startSet || endSet);
  };

  // Whether to show the per-frequency Lock control: only once the org has actually
  // configured a schedule — a fixed charge day, or a non-empty start/end window.
  const shouldShowLock = (freq: RecurringFrequency): boolean =>
    isFixedChargeDayEnabled(freq) || hasWindowValue(freq);

  // Default anchor value seeded when the fixed-day section is first toggled on,
  // per frequency. ANNUALLY uses "MM-DD"; MONTHLY uses 1; WEEKLY uses 1 (Monday).
  const defaultFixedChargeDayFor = (freq: RecurringFrequency): string | number => {
    if (freq === 'Annually') return '01-01';
    if (freq === 'Weekly') return 1;
    return 1; // Monthly
  };

  // Toggle the fixed-day section. Off => clear fixedChargeDay (derive-from-data).
  // When turning off leaves NO configured schedule (window also empty), also clear
  // the lock so it can't silently persist/re-apply (Lock defaults unchecked).
  const toggleFixedChargeDay = (freq: RecurringFrequency, enabled: boolean) => {
    if (enabled) {
      updateScheduleConfigForFrequency(freq, 'fixedChargeDay', defaultFixedChargeDayFor(freq));
    } else {
      const updates: Partial<ScheduledRangeConfig> = { fixedChargeDay: null };
      if (!hasWindowValue(freq)) updates.lockedScheduleDate = false;
      updateScheduleConfigMultipleFields(freq, updates);
    }
  };

  // Toggle the start/end window section. Off => reset both date types to 'none'
  // and clear their values. When turning off leaves NO configured schedule
  // (fixed day also off), also clear the lock AND the time — otherwise a
  // previously-chosen scheduledTime would persist invisibly (the Default Time
  // picker is hidden once the window is off). When the fixed-day section is still
  // on, leave scheduledTime/lock intact — the fixed-day section uses them.
  const toggleScheduleWindow = (freq: RecurringFrequency, enabled: boolean) => {
    if (enabled) {
      updateScheduleConfigMultipleFields(freq, { startDateType: 'dynamic', endDateType: 'none' });
    } else {
      updateScheduleConfigMultipleFields(freq, {
        startDateType: 'none',
        endDateType: 'none',
        startDateOffset: null,
        endDateOffset: null,
        fixedStartDate: null,
        fixedEndDate: null,
        ...(isFixedChargeDayEnabled(freq) ? {} : { lockedScheduleDate: false, scheduledTime: '' }),
      });
    }
  };

  // Human-readable summary of the fixed-day anchor for the bottom summary card.
  const describeFixedChargeDay = (freq: RecurringFrequency): string | null => {
    const value = getFixedChargeDay(freq);
    if (value === null || value === undefined || value === '') return null;
    if (freq === 'Weekly') {
      const idx = parseInt(String(value), 10);
      return Number.isNaN(idx) ? null : `every ${WEEKDAY_LABELS[idx]}`;
    }
    if (freq === 'Monthly') {
      const day = parseInt(String(value), 10);
      if (Number.isNaN(day)) return null;
      return day === 31
        ? 'the last day of every month'
        : `the ${ordinalDayLabel(day)} of every month`;
    }
    if (freq === 'Annually') {
      const match = String(value).match(/^(\d{1,2})-(\d{1,2})$/);
      if (!match) return null;
      const month = parseInt(match[1], 10);
      const day = parseInt(match[2], 10);
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      return `${MONTH_LABELS[month - 1]} ${day} every year`;
    }
    return null;
  };

  // Type for schedule validation errors
  type ScheduleValidationError =
    | null
    | 'start_date_missing'
    | 'end_date_missing'
    | 'start_date_in_past'
    | 'end_date_in_past'
    | 'start_after_end';

  // Helper to check if a date has passed (is before today)
  const isDatePassed = (dateString: string): boolean => {
    const date = dayjs(dateString);
    const today = dayjs().startOf('day');
    return date.isBefore(today);
  };

  // Helper to get schedule validation error type (null = no error)
  const getScheduleValidationError = (freq: RecurringFrequency): ScheduleValidationError => {
    const config = frequencyScheduledRanges?.[freq];
    if (!config) return null;

    const startType = config.startDateType || 'none';
    const endType = config.endDateType || 'none';

    // Check for incomplete configuration - user selected type but didn't provide value
    // Start date: dynamic selected but no offset, or fixed selected but no date
    if (startType === 'dynamic' && config.startDateOffset == null) {
      return 'start_date_missing';
    }
    if (startType === 'fixed' && !config.fixedStartDate) {
      return 'start_date_missing';
    }

    // End date: dynamic selected but no offset, or fixed selected but no date
    if (endType === 'dynamic' && config.endDateOffset == null) {
      return 'end_date_missing';
    }
    if (endType === 'fixed' && !config.fixedEndDate) {
      return 'end_date_missing';
    }

    // Check for past fixed dates (these will be silently ignored at runtime)
    if (startType === 'fixed' && config.fixedStartDate && isDatePassed(config.fixedStartDate)) {
      return 'start_date_in_past';
    }
    if (endType === 'fixed' && config.fixedEndDate && isDatePassed(config.fixedEndDate)) {
      return 'end_date_in_past';
    }

    // Now check date ordering (only if both have values)
    // Case 1: Both are dynamic - compare offsets
    if (startType === 'dynamic' && endType === 'dynamic') {
      if (
        config.startDateOffset != null &&
        config.endDateOffset != null &&
        config.startDateOffset >= config.endDateOffset
      ) {
        return 'start_after_end';
      }
    }

    // Case 2: Both are fixed - compare dates
    if (startType === 'fixed' && endType === 'fixed') {
      if (config.fixedStartDate && config.fixedEndDate) {
        if (!dayjs(config.fixedStartDate).isBefore(dayjs(config.fixedEndDate))) {
          return 'start_after_end';
        }
      }
    }

    // Case 3: Start is dynamic, end is fixed - calculate dynamic start and compare
    if (startType === 'dynamic' && endType === 'fixed') {
      if (config.startDateOffset != null && config.fixedEndDate) {
        const unit = (config.scheduleType || 'days').slice(0, -1) as dayjs.ManipulateType;
        const calculatedStart = dayjs().add(config.startDateOffset, unit);
        if (!calculatedStart.isBefore(dayjs(config.fixedEndDate))) {
          return 'start_after_end';
        }
      }
    }

    // Case 4: Start is fixed, end is dynamic - calculate dynamic end and compare
    if (startType === 'fixed' && endType === 'dynamic') {
      if (config.fixedStartDate && config.endDateOffset != null) {
        const unit = (config.scheduleType || 'days').slice(0, -1) as dayjs.ManipulateType;
        const calculatedEnd = dayjs().add(config.endDateOffset, unit);
        if (!dayjs(config.fixedStartDate).isBefore(calculatedEnd)) {
          return 'start_after_end';
        }
      }
    }

    return null;
  };

  // Helper to get validation error message for display
  const getScheduleValidationMessage = (freq: RecurringFrequency): string | null => {
    const error = getScheduleValidationError(freq);
    switch (error) {
      case 'start_date_missing':
        return 'Please select a value for Default Start Date or set to None';
      case 'end_date_missing':
        return 'Please select a value for Default End Date or set to None';
      case 'start_date_in_past':
        return 'Default Start Date is in the past and will be ignored';
      case 'end_date_in_past':
        return 'Default End Date is in the past and will be ignored';
      case 'start_after_end':
        return 'Default Start Date must be before Default End Date';
      default:
        return null;
    }
  };

  // Helper function to convert frequency name to validation field format for a specific amount
  const getValidationFieldName = (frequency: string, index: number, amountField: string) => {
    const normalizedFreq = frequency.toLowerCase().replace('-', '');
    return `form_configuration-amounts-${normalizedFreq}-${index}-${amountField}`;
  };

  // Get current frequency's values for Amount step
  const getCurrentAmountValues = () => {
    if (
      activeAmountTab &&
      frequencyAmounts &&
      frequencyAmounts[activeAmountTab as keyof FrequencyAmountsMap]
    ) {
      return frequencyAmounts[activeAmountTab as keyof FrequencyAmountsMap];
    }
    return {
      amount1: '',
      amount2: '',
      amount3: '',
      amount4: '',
      defaultAmount: '',
    };
  };

  const getAmountsForFrequency = (freq: string) => ({
    amount1: frequencyAmounts?.[freq as keyof FrequencyAmountsMap]?.amount1 || '',
    amount2: frequencyAmounts?.[freq as keyof FrequencyAmountsMap]?.amount2 || '',
    amount3: frequencyAmounts?.[freq as keyof FrequencyAmountsMap]?.amount3 || '',
    amount4: frequencyAmounts?.[freq as keyof FrequencyAmountsMap]?.amount4 || '',
    defaultAmount: frequencyAmounts?.[freq as keyof FrequencyAmountsMap]?.defaultAmount || '',
  });

  const hasAmountValue = (value?: string | null) =>
    typeof value === 'string' && value.trim() !== '';

  const getDefaultStandardAmounts = () => ({
    amount1: '25',
    amount2: '50',
    amount3: '100',
    amount4: '250',
    defaultAmount: '50',
  });

  const reconcileStandardFrequencyFromExistingData = (targetFreq: string) => {
    if (!setFrequencyAmounts || !frequencyAmounts) return;

    const targetAmounts = getAmountsForFrequency(targetFreq);
    const nextAmounts = { ...targetAmounts };
    let hasChanges = false;

    const amountFields: Array<keyof ReturnType<typeof getAmountsForFrequency>> = [
      'amount1',
      'amount2',
      'amount3',
      'amount4',
      'defaultAmount',
    ];

    amountFields.forEach((field) => {
      if (hasAmountValue(targetAmounts[field])) return;

      const sourceValue = frequencyOrder
        .filter((freq) => freq !== targetFreq)
        .map((freq) => getAmountsForFrequency(freq)[field])
        .find((value) => hasAmountValue(value));

      if (sourceValue) {
        nextAmounts[field] = sourceValue;
        hasChanges = true;
      }
    });

    const hasConfiguredSourceFrequency = frequencyOrder
      .filter((freq) => freq !== targetFreq)
      .some((freq) =>
        amountFields.some((field) => hasAmountValue(getAmountsForFrequency(freq)[field])),
      );

    if (
      !hasConfiguredSourceFrequency &&
      amountFields.every((field) => !hasAmountValue(nextAmounts[field]))
    ) {
      const defaultAmounts = getDefaultStandardAmounts();
      amountFields.forEach((field) => {
        nextAmounts[field] = defaultAmounts[field];
      });
      hasChanges = true;
    }

    if (hasChanges) {
      setFrequencyAmounts({
        ...frequencyAmounts,
        [targetFreq]: nextAmounts,
      } as FrequencyAmountsMap);
    }
  };

  const handleStandardAmountTabChange = (freq: string, index: number) => {
    reconcileStandardFrequencyFromExistingData(freq);
    setActiveAmountTab(freq);
    setActiveAmountTabIndex(index);
  };

  // Update amount for current frequency
  const updateAmountForFrequency = (field: string, value: string) => {
    // Validate and sanitize numeric input with up to 2 decimal places
    let processedValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
    processedValue = processedValue.replace(/(\.\d{2})\d+$/, '$1');

    // Enforce maximum of $1,000,000
    if (processedValue && parseFloat(processedValue) > 1000000) {
      processedValue = '1000000';
    }

    // Determine the target frequency based on form type
    const targetFrequency =
      formType === 'CROWDFUNDING_FORM' ? activeCrowdfundingTab : activeAmountTab;

    if (!targetFrequency) return; // Guard against empty frequency

    if (setFrequencyAmounts && frequencyAmounts) {
      setFrequencyAmounts({
        ...frequencyAmounts,
        [targetFrequency]: {
          ...(frequencyAmounts[targetFrequency as keyof FrequencyAmountsMap] || {}),
          [field]: processedValue,
        },
      } as FrequencyAmountsMap);
    }
  };

  // Handle form type change and reset amounts
  const handleFormTypeChange = (newFormType: string) => {
    setFormType(newFormType);
    if (newFormType === 'EMBED_FORM' || newFormType === 'CROWDFUNDING_FORM') {
      setFundraiserType('DONATION_FORM');
    } else if (newFormType === 'PRODUCT') {
      setFundraiserType('PRODUCT');
    }

    // Auto-reset Items metric when switching away from Sponsorship form type
    // Stay in Count mode (switch to Subscriptions) rather than resetting to Dollar Amount
    if (newFormType !== 'PRODUCT' && trackingMetric === 'PRODUCT_COUNT') {
      setTrackingMetric('SUBSCRIPTION_COUNT');
      setCountOffset('0');
    }

    // Clear validation errors from the previous form type
    setInvalidFields((prevInvalidFields) => {
      const newInvalidFields = new Set(prevInvalidFields);
      // Remove all amounts-related validation errors
      for (const field of newInvalidFields) {
        if (field.startsWith('form_configuration-amounts-')) {
          newInvalidFields.delete(field);
        }
      }
      return newInvalidFields;
    });

    // Reset the giving levels limit warning when switching away from crowdfunding
    if (newFormType !== 'CROWDFUNDING_FORM') {
      setGivingLevelsLimitReached(false);
    }
    // Reset the sponsorship groups limit warning when switching away from product
    if (newFormType !== 'PRODUCT') {
      setSponsorshipGroupsLimitReached(false);
    }

    // Initialize amounts based on form type (only if data doesn't exist)
    if (newFormType === 'EMBED_FORM' && setFrequencyAmounts) {
      // Check if Standard amounts already exist
      const hasExistingStandardData =
        frequencyAmounts &&
        (frequencyAmounts['One-Time']?.amount1 ||
          frequencyAmounts.Daily?.amount1 ||
          frequencyAmounts.Weekly?.amount1 ||
          frequencyAmounts.Monthly?.amount1 ||
          frequencyAmounts.Annually?.amount1);

      if (!hasExistingStandardData) {
        // Initialize Standard amounts - only prefill for selected frequencies
        const defaultAmounts = {
          amount1: '25',
          amount2: '50',
          amount3: '100',
          amount4: '250',
          defaultAmount: '50',
        };

        const emptyAmounts = {
          amount1: '',
          amount2: '',
          amount3: '',
          amount4: '',
          defaultAmount: '',
        };

        const newFrequencyAmounts: FrequencyAmountsMap = {
          'One-Time': checkedValues.includes('One-Time') ? defaultAmounts : emptyAmounts,
          Daily: checkedValues.includes('Daily') ? defaultAmounts : emptyAmounts,
          Weekly: checkedValues.includes('Weekly') ? defaultAmounts : emptyAmounts,
          Monthly: checkedValues.includes('Monthly') ? defaultAmounts : emptyAmounts,
          Annually: checkedValues.includes('Annually') ? defaultAmounts : emptyAmounts,
        };
        setFrequencyAmounts(newFrequencyAmounts);
      }
    } else if (newFormType === 'CROWDFUNDING_FORM' && setCrowdfundingOptions) {
      // Only initialize if data doesn't already exist (preserve existing data when switching back)
      const hasExistingData =
        (oneTimeCrowdfundingOptions && oneTimeCrowdfundingOptions.length > 0) ||
        (dailyCrowdfundingOptions && dailyCrowdfundingOptions.length > 0) ||
        (weeklyCrowdfundingOptions && weeklyCrowdfundingOptions.length > 0) ||
        (monthlyCrowdfundingOptions && monthlyCrowdfundingOptions.length > 0) ||
        (annuallyCrowdfundingOptions && annuallyCrowdfundingOptions.length > 0);

      if (!hasExistingData) {
        // Initialize with one empty crowdfunding option (legacy structure - for backward compatibility)
        setCrowdfundingOptions([
          {
            id: Math.random().toString(36).substring(2, 9),
            title: 'Sponsor a Child',
            amount: '25',
            description: 'Cover the daily meal of a single child.',
          },
        ]);

        // Initialize per-frequency crowdfunding options with one default giving level
        const defaultGivingLevel = {
          id: uuidv4(),
          title: 'Sponsor a Child',
          amount: '25',
          description: 'Cover the daily meal of a single child.',
        };

        if (setOneTimeCrowdfundingOptions)
          setOneTimeCrowdfundingOptions([{ ...defaultGivingLevel, id: uuidv4() }]);
        if (setDailyCrowdfundingOptions)
          setDailyCrowdfundingOptions([{ ...defaultGivingLevel, id: uuidv4() }]);
        if (setWeeklyCrowdfundingOptions)
          setWeeklyCrowdfundingOptions([{ ...defaultGivingLevel, id: uuidv4() }]);
        if (setMonthlyCrowdfundingOptions)
          setMonthlyCrowdfundingOptions([{ ...defaultGivingLevel, id: uuidv4() }]);
        if (setAnnuallyCrowdfundingOptions)
          setAnnuallyCrowdfundingOptions([{ ...defaultGivingLevel, id: uuidv4() }]);
      }
      // Note: Do NOT clear frequencyAmounts, productGroups, or per-frequency product groups
      // All form type data persists so users can switch between form types without data loss
    } else if (newFormType === 'PRODUCT' && setProductGroups) {
      // Only initialize if data doesn't already exist (preserve existing data when switching back)
      const hasExistingData =
        (oneTimeProductGroups && oneTimeProductGroups.length > 0) ||
        (dailyProductGroups && dailyProductGroups.length > 0) ||
        (weeklyProductGroups && weeklyProductGroups.length > 0) ||
        (monthlyProductGroups && monthlyProductGroups.length > 0) ||
        (annuallyProductGroups && annuallyProductGroups.length > 0);

      if (!hasExistingData) {
        // Initialize with one empty product group (legacy structure - for backward compatibility)
        setProductGroups([
          {
            id: Math.random().toString(36).substring(2, 9),
            productGroupName: 'Sponsor a Child',
            productGroupDescription: 'Covers the education of a child.',
            products: [
              {
                productID: Math.random().toString(36).substring(2, 9),
                productName: '1 Child',
                productPrice: '25',
              },
            ],
          },
        ]);

        // Initialize per-frequency product groups with one default sponsorship item
        const defaultProductGroup = {
          id: uuidv4(),
          productGroupName: 'Sponsor a Child',
          productGroupDescription: 'Covers the education of a child.',
          products: [
            {
              productID: uuidv4(),
              productName: '1 Child',
              productPrice: '25',
            },
          ],
        };

        if (setOneTimeProductGroups)
          setOneTimeProductGroups([
            {
              ...defaultProductGroup,
              id: uuidv4(),
              products: [{ ...defaultProductGroup.products[0], productID: uuidv4() }],
            },
          ]);
        if (setDailyProductGroups)
          setDailyProductGroups([
            {
              ...defaultProductGroup,
              id: uuidv4(),
              products: [{ ...defaultProductGroup.products[0], productID: uuidv4() }],
            },
          ]);
        if (setWeeklyProductGroups)
          setWeeklyProductGroups([
            {
              ...defaultProductGroup,
              id: uuidv4(),
              products: [{ ...defaultProductGroup.products[0], productID: uuidv4() }],
            },
          ]);
        if (setMonthlyProductGroups)
          setMonthlyProductGroups([
            {
              ...defaultProductGroup,
              id: uuidv4(),
              products: [{ ...defaultProductGroup.products[0], productID: uuidv4() }],
            },
          ]);
        if (setAnnuallyProductGroups)
          setAnnuallyProductGroups([
            {
              ...defaultProductGroup,
              id: uuidv4(),
              products: [{ ...defaultProductGroup.products[0], productID: uuidv4() }],
            },
          ]);
      }
      // Note: Do NOT clear frequencyAmounts, crowdfundingOptions, or per-frequency crowdfunding options
      // All form type data persists so users can switch between form types without data loss
    }
  };

  // Crowdfunding Options Handlers
  const addCrowdfundingOption = () => {
    if (setCrowdfundingOptions && crowdfundingOptions) {
      if (crowdfundingOptions.length < 4) {
        setCrowdfundingOptions([
          ...crowdfundingOptions,
          {
            id: Math.random().toString(36).substring(2, 9),
            title: '',
            amount: '',
            description: '',
          },
        ]);
      } else {
        setGivingLevelsLimitReached(true);
      }
    }
  };

  const removeCrowdfundingOption = (index: number) => {
    if (setCrowdfundingOptions && crowdfundingOptions) {
      const newOptions = [...crowdfundingOptions];
      newOptions.splice(index, 1);
      setCrowdfundingOptions(newOptions);
      setGivingLevelsLimitReached(false);
    }
  };

  const updateCrowdfundingOption = (
    index: number,
    field: keyof CrowdfundingOption,
    value: string,
  ) => {
    if (setCrowdfundingOptions && crowdfundingOptions) {
      const newOptions = [...crowdfundingOptions];

      // For amount field, validate and ensure positive non-zero numbers only
      if (field === 'amount') {
        // Allow only numeric input with up to 2 decimal places
        let processedValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
        processedValue = processedValue.replace(/(\.\d{2})\d+$/, '$1');

        // Enforce maximum of $1,000,000
        if (processedValue && parseFloat(processedValue) > 1000000) {
          processedValue = '1000000';
        }

        newOptions[index] = { ...newOptions[index], [field]: processedValue };
      } else {
        newOptions[index] = { ...newOptions[index], [field]: value };
      }

      setCrowdfundingOptions(newOptions);
    }
  };

  // Per-Frequency Crowdfunding Options Helpers (NEW - mirrors Standard form pattern)
  const getFrequencySetterMap = () => ({
    'One-Time': setOneTimeCrowdfundingOptions,
    Daily: setDailyCrowdfundingOptions,
    Weekly: setWeeklyCrowdfundingOptions,
    Monthly: setMonthlyCrowdfundingOptions,
    Annually: setAnnuallyCrowdfundingOptions,
  });

  const getFrequencyOptionsMap = () => ({
    'One-Time': oneTimeCrowdfundingOptions,
    Daily: dailyCrowdfundingOptions,
    Weekly: weeklyCrowdfundingOptions,
    Monthly: monthlyCrowdfundingOptions,
    Annually: annuallyCrowdfundingOptions,
  });

  const getCrowdfundingOptionsForFrequency = (freq: string): CrowdfundingOption[] => {
    const optionsMap = getFrequencyOptionsMap();
    return (optionsMap[freq as keyof typeof optionsMap] || []) as CrowdfundingOption[];
  };

  const hasCrowdfundingOptionData = (option?: CrowdfundingOption) =>
    !!option &&
    (hasAmountValue(option.title) ||
      hasAmountValue(option.amount) ||
      hasAmountValue(option.description));

  const cloneCrowdfundingOption = (option: CrowdfundingOption): CrowdfundingOption => ({
    id: uuidv4(),
    title: option.title,
    amount: option.amount,
    description: option.description,
  });

  const reconcileCrowdfundingFrequencyFromExistingData = (targetFreq: string) => {
    const setterMap = getFrequencySetterMap();
    const setter = setterMap[targetFreq as keyof typeof setterMap];
    const targetOptions = getCrowdfundingOptionsForFrequency(targetFreq);

    if (!setter) return;

    const sourceFrequencies = frequencyOrder.filter((freq) => freq !== targetFreq);
    const maxOptionCount = Math.max(
      targetOptions.length,
      ...sourceFrequencies.map((freq) => getCrowdfundingOptionsForFrequency(freq).length),
    );

    const reconciledOptions = [...targetOptions];
    let hasChanges = false;

    for (let optionIndex = 0; optionIndex < maxOptionCount; optionIndex += 1) {
      const sourceOption = sourceFrequencies
        .map((freq) => getCrowdfundingOptionsForFrequency(freq)[optionIndex])
        .find((option) => hasCrowdfundingOptionData(option));

      if (!sourceOption) continue;

      const currentOption = reconciledOptions[optionIndex];

      if (!currentOption) {
        reconciledOptions[optionIndex] = cloneCrowdfundingOption(sourceOption);
        hasChanges = true;
        continue;
      }

      let optionChanged = false;
      let nextOption = currentOption;

      if (!hasAmountValue(currentOption.title) && hasAmountValue(sourceOption.title)) {
        nextOption = { ...nextOption, title: sourceOption.title };
        optionChanged = true;
      }

      if (!hasAmountValue(currentOption.amount) && hasAmountValue(sourceOption.amount)) {
        nextOption = { ...nextOption, amount: sourceOption.amount };
        optionChanged = true;
      }

      if (!hasAmountValue(currentOption.description) && hasAmountValue(sourceOption.description)) {
        nextOption = { ...nextOption, description: sourceOption.description };
        optionChanged = true;
      }

      if (optionChanged) {
        reconciledOptions[optionIndex] = nextOption;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setter(reconciledOptions);
      clearValidationErrorsForFrequency(targetFreq);
    }
  };

  const handleCrowdfundingTabChange = (newFreq: string) => {
    reconcileCrowdfundingFrequencyFromExistingData(newFreq);
    setActiveCrowdfundingTab(newFreq);
  };

  const updateTitleForFrequency = (freq: string, index: number, value: string) => {
    const setterMap = getFrequencySetterMap();
    const optionsMap = getFrequencyOptionsMap();
    const options = optionsMap[freq as keyof typeof optionsMap] as CrowdfundingOption[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (options && setter && options[index]) {
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], title: value };
      setter(newOptions);
    }
  };

  const updateCrowdfundingOptionAmount = (freq: string, index: number, value: string) => {
    const setterMap = getFrequencySetterMap();
    const optionsMap = getFrequencyOptionsMap();
    const options = optionsMap[freq as keyof typeof optionsMap] as CrowdfundingOption[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (options && setter && options[index]) {
      // Validate and ensure positive non-zero numbers only
      let processedValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
      processedValue = processedValue.replace(/(\.\d{2})\d+$/, '$1');

      // Enforce maximum of $1,000,000
      if (processedValue && parseFloat(processedValue) > 1000000) {
        processedValue = '1000000';
      }

      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], amount: processedValue };
      setter(newOptions);
    }
  };

  const updateDescriptionForFrequency = (freq: string, index: number, value: string) => {
    const setterMap = getFrequencySetterMap();
    const optionsMap = getFrequencyOptionsMap();
    const options = optionsMap[freq as keyof typeof optionsMap] as CrowdfundingOption[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (options && setter && options[index]) {
      const newOptions = [...options];
      newOptions[index] = { ...newOptions[index], description: value };
      setter(newOptions);
    }
  };

  const removeCrowdfundingOptionAcrossAllFrequencies = (index: number) => {
    const allFreqs = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    const setterMap = getFrequencySetterMap();
    const optionsMap = getFrequencyOptionsMap();

    allFreqs.forEach((freq) => {
      const options = optionsMap[freq as keyof typeof optionsMap] as
        | CrowdfundingOption[]
        | undefined;
      const setter = setterMap[freq as keyof typeof setterMap];

      if (options && setter && options[index]) {
        const newOptions = [...options];
        newOptions.splice(index, 1);
        setter(newOptions);
      }
    });

    setGivingLevelsLimitReached(false);
  };

  const addCrowdfundingOptionAcrossAllFrequencies = () => {
    // Adds a new giving level to ALL frequencies to maintain structural consistency
    // Title is shared, but amount and description are per-frequency
    const allFreqs = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    const setterMap = getFrequencySetterMap();
    const optionsMap = getFrequencyOptionsMap();

    // Check if any frequency has reached the limit
    const hasReachedLimit = allFreqs.some((freq) => {
      const options = optionsMap[freq as keyof typeof optionsMap] as
        | CrowdfundingOption[]
        | undefined;
      return options && options.length >= 4;
    });

    if (hasReachedLimit) {
      setGivingLevelsLimitReached(true);
      return;
    }

    allFreqs.forEach((freq) => {
      const options = optionsMap[freq as keyof typeof optionsMap] as
        | CrowdfundingOption[]
        | undefined;
      const setter = setterMap[freq as keyof typeof setterMap];

      if (options && setter) {
        setter([
          ...options,
          {
            id: Math.random().toString(36).substring(2, 9),
            title: '',
            amount: '', // Per-frequency
            description: '', // Per-frequency
          },
        ]);
      }
    });

    setGivingLevelsLimitReached(false);
  };

  // Per-Frequency Product Groups Helpers (NEW - mirrors Standard form pattern)
  const getProductGroupFrequencySetterMap = () => ({
    'One-Time': setOneTimeProductGroups,
    Daily: setDailyProductGroups,
    Weekly: setWeeklyProductGroups,
    Monthly: setMonthlyProductGroups,
    Annually: setAnnuallyProductGroups,
  });

  const getProductGroupFrequencyOptionsMap = () => ({
    'One-Time': oneTimeProductGroups,
    Daily: dailyProductGroups,
    Weekly: weeklyProductGroups,
    Monthly: monthlyProductGroups,
    Annually: annuallyProductGroups,
  });

  const getProductGroupsForFrequency = (freq: string): ProductGroup[] => {
    const optionsMap = getProductGroupFrequencyOptionsMap();
    return (optionsMap[freq as keyof typeof optionsMap] || []) as ProductGroup[];
  };

  const hasTextValue = (value?: string | null) => typeof value === 'string' && value.trim() !== '';

  const hasProductData = (product?: ProductOption) =>
    !!product && (hasTextValue(product.productName) || hasTextValue(product.productPrice));

  const hasProductGroupData = (group?: ProductGroup) =>
    !!group &&
    (hasTextValue(group.productGroupName) ||
      hasTextValue(group.productGroupDescription) ||
      group.products.some((product) => hasProductData(product)));

  const cloneProductOption = (product: ProductOption): ProductOption => ({
    productID: uuidv4(),
    productName: product.productName,
    productPrice: product.productPrice,
  });

  const cloneProductGroup = (group: ProductGroup): ProductGroup => ({
    id: uuidv4(),
    productGroupName: group.productGroupName,
    productGroupDescription: group.productGroupDescription,
    products: group.products.map((product) => cloneProductOption(product)),
  });

  const reconcileSponsorshipFrequencyFromExistingData = (targetFreq: string) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const setter = setterMap[targetFreq as keyof typeof setterMap];
    const targetGroups = getProductGroupsForFrequency(targetFreq);

    if (!setter) return;

    const sourceFrequencies = frequencyOrder.filter((freq) => freq !== targetFreq);
    const maxGroupCount = Math.max(
      targetGroups.length,
      ...sourceFrequencies.map((freq) => getProductGroupsForFrequency(freq).length),
    );

    const reconciledGroups = [...targetGroups];
    let hasChanges = false;

    for (let groupIndex = 0; groupIndex < maxGroupCount; groupIndex += 1) {
      const sourceGroup = sourceFrequencies
        .map((freq) => getProductGroupsForFrequency(freq)[groupIndex])
        .find((group) => hasProductGroupData(group));

      if (!sourceGroup) continue;

      const currentGroup = reconciledGroups[groupIndex];

      if (!currentGroup) {
        reconciledGroups[groupIndex] = cloneProductGroup(sourceGroup);
        hasChanges = true;
        continue;
      }

      let groupChanged = false;
      let nextGroup = currentGroup;

      if (
        !hasTextValue(currentGroup.productGroupName) &&
        hasTextValue(sourceGroup.productGroupName)
      ) {
        nextGroup = { ...nextGroup, productGroupName: sourceGroup.productGroupName };
        groupChanged = true;
      }

      if (
        !hasTextValue(currentGroup.productGroupDescription) &&
        hasTextValue(sourceGroup.productGroupDescription)
      ) {
        nextGroup = {
          ...nextGroup,
          productGroupDescription: sourceGroup.productGroupDescription,
        };
        groupChanged = true;
      }

      const currentProducts = nextGroup.products || [];
      const maxProductCount = Math.max(
        currentProducts.length,
        ...sourceFrequencies.map(
          (freq) => getProductGroupsForFrequency(freq)[groupIndex]?.products.length || 0,
        ),
      );

      const nextProducts = [...currentProducts];

      for (let productIndex = 0; productIndex < maxProductCount; productIndex += 1) {
        const sourceProduct = sourceFrequencies
          .map((freq) => getProductGroupsForFrequency(freq)[groupIndex]?.products[productIndex])
          .find((product) => hasProductData(product));

        if (!sourceProduct) continue;

        const currentProduct = nextProducts[productIndex];

        if (!currentProduct) {
          nextProducts[productIndex] = cloneProductOption(sourceProduct);
          groupChanged = true;
          continue;
        }

        let productChanged = false;
        let nextProduct = currentProduct;

        if (!hasTextValue(currentProduct.productName) && hasTextValue(sourceProduct.productName)) {
          nextProduct = { ...nextProduct, productName: sourceProduct.productName };
          productChanged = true;
        }

        if (
          !hasTextValue(currentProduct.productPrice) &&
          hasTextValue(sourceProduct.productPrice)
        ) {
          nextProduct = { ...nextProduct, productPrice: sourceProduct.productPrice };
          productChanged = true;
        }

        if (productChanged) {
          nextProducts[productIndex] = nextProduct;
          groupChanged = true;
        }
      }

      if (groupChanged) {
        nextGroup = { ...nextGroup, products: nextProducts };
        reconciledGroups[groupIndex] = nextGroup;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setter(reconciledGroups);
      clearSponsorshipValidationErrorsForFrequency(targetFreq);
    }
  };

  const handleSponsorshipTabChange = (newFreq: string) => {
    reconcileSponsorshipFrequencyFromExistingData(newFreq);
    setActiveSponsorshipTab(newFreq);
  };

  const updateProductGroupNameForFrequency = (freq: string, index: number, newName: string) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter && groups[index]) {
      const newGroups = [...groups];
      newGroups[index] = { ...newGroups[index], productGroupName: newName };
      setter(newGroups);
    }
  };

  const updateProductGroupDescriptionForFrequency = (
    freq: string,
    index: number,
    value: string,
  ) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter && groups[index]) {
      const newGroups = [...groups];
      newGroups[index] = { ...newGroups[index], productGroupDescription: value };
      setter(newGroups);
    }
  };

  const updateProductNameForFrequency = (
    freq: string,
    groupIndex: number,
    productIndex: number,
    value: string,
  ) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter && groups[groupIndex] && groups[groupIndex].products[productIndex]) {
      const newGroups = [...groups];
      const newProducts = [...newGroups[groupIndex].products];
      newProducts[productIndex] = { ...newProducts[productIndex], productName: value };
      newGroups[groupIndex] = { ...newGroups[groupIndex], products: newProducts };
      setter(newGroups);
    }
  };

  // Helper function to format price with commas for display
  const formatPriceWithCommas = (value: string): string => {
    if (!value) return '';
    // Split by decimal point
    const parts = value.split('.');
    // Add commas to integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Rejoin with decimal if it exists
    return parts.join('.');
  };

  const updateProductPriceForFrequency = (
    freq: string,
    groupIndex: number,
    productIndex: number,
    value: string,
  ) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter && groups[groupIndex] && groups[groupIndex].products[productIndex]) {
      // Remove all non-numeric characters except decimal point (including commas)
      let processedValue = value.replace(/[^\d.]/g, '');

      // Ensure only one decimal point
      processedValue = processedValue.replace(/(\..*)\./g, '$1');

      // Limit to 2 decimal places
      processedValue = processedValue.replace(/(\.\d{2})\d+$/, '$1');

      // Cap at $1,000,000
      if (processedValue && parseFloat(processedValue) > 1000000) {
        processedValue = '1000000';
      }

      const newGroups = [...groups];
      const newProducts = [...newGroups[groupIndex].products];
      newProducts[productIndex] = { ...newProducts[productIndex], productPrice: processedValue };
      newGroups[groupIndex] = { ...newGroups[groupIndex], products: newProducts };
      setter(newGroups);
    }
  };

  const addProductGroupForFrequency = (freq: string) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    // Debug logging
    console.log('activeSponsorshipTab (freq):', freq);
    console.log('setter exists:', !!setter);
    console.log('groups:', groups);
    console.log('groups length:', groups?.length);
    console.log('setterMap:', setterMap);

    if (groups && setter) {
      console.log('✅ Adding new product group');
      setter([
        ...groups,
        {
          id: Math.random().toString(36).substring(2, 9),
          productGroupName: '',
          productGroupDescription: '',
          products: [
            {
              productID: Math.random().toString(36).substring(2, 9),
              productName: '',
              productPrice: '',
            },
          ],
        },
      ]);
    } else {
      console.log('❌ Condition failed - setter or groups invalid');
      console.log('Reason: setter =', !!setter, ', groups =', !!groups);
    }
  };

  const removeProductGroupForFrequency = (freq: string, index: number) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter) {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      setter(newGroups);
    }
  };

  const MAX_SPONSORSHIP_GROUPS = 4;

  const addProductGroupAcrossAllFrequencies = () => {
    // Adds a new product group to ALL frequencies to maintain structural consistency
    const allFreqs = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();

    // Enforce limit of MAX_SPONSORSHIP_GROUPS in create mode only.
    // Edit mode is unrestricted because existing forms may already have more.
    if (!isEditMode) {
      const hasReachedLimit = allFreqs.some((freq) => {
        const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
        return groups && groups.length >= MAX_SPONSORSHIP_GROUPS;
      });

      if (hasReachedLimit) {
        setSponsorshipGroupsLimitReached(true);
        return;
      }
    }

    allFreqs.forEach((freq) => {
      const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
      const setter = setterMap[freq as keyof typeof setterMap];

      if (groups && setter) {
        setter([
          ...groups,
          {
            id: uuidv4(), // Unique ID for each frequency
            productGroupName: '',
            productGroupDescription: '',
            products: [
              {
                productID: uuidv4(), // Unique ID for each frequency
                productName: '',
                productPrice: '',
              },
            ],
          },
        ]);
      }
    });

    setSponsorshipGroupsLimitReached(false);
  };

  const removeProductGroupAcrossAllFrequencies = (index: number) => {
    // Removes product group from ALL frequencies to maintain structural consistency
    const allFreqs = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();

    allFreqs.forEach((freq) => {
      const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
      const setter = setterMap[freq as keyof typeof setterMap];

      if (groups && setter && groups[index]) {
        const newGroups = [...groups];
        newGroups.splice(index, 1);
        setter(newGroups);
      }
    });
    setSponsorshipGroupsLimitReached(false);
  };

  const addProductAcrossAllFrequencies = (groupIndex: number) => {
    // Adds a new product option to a specific group across ALL frequencies
    // Product name is shared, but price is per-frequency
    const allFreqs = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();

    allFreqs.forEach((freq) => {
      const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
      const setter = setterMap[freq as keyof typeof setterMap];

      if (groups && setter && groups[groupIndex]) {
        const newGroups = [...groups];
        const newProduct = {
          productID: Math.random().toString(36).substring(2, 9),
          productName: '',
          productPrice: '', // Per-frequency
        };
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          products: [...newGroups[groupIndex].products, newProduct],
        };
        setter(newGroups);
      }
    });

    // Set the active tab to the newly added product
    const currentGroups = getProductGroupsForFrequency(activeSponsorshipTab);
    if (currentGroups[groupIndex]) {
      setActiveProductTabs({
        ...activeProductTabs,
        [groupIndex]: currentGroups[groupIndex].products.length,
      });
    }
  };

  const removeProductAcrossAllFrequencies = (groupIndex: number, productIndex: number) => {
    // Removes a product option from a specific group across ALL frequencies
    const allFreqs = ['One-Time', 'Daily', 'Weekly', 'Monthly', 'Annually'];
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();

    allFreqs.forEach((freq) => {
      const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
      const setter = setterMap[freq as keyof typeof setterMap];

      if (groups && setter && groups[groupIndex] && groups[groupIndex].products[productIndex]) {
        const newGroups = [...groups];
        const newProducts = [...newGroups[groupIndex].products];
        newProducts.splice(productIndex, 1);
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          products: newProducts,
        };
        setter(newGroups);
      }
    });

    // Adjust active tab if needed - calculate length from post-removal array
    const currentGroups = getProductGroupsForFrequency(activeSponsorshipTab);
    if (currentGroups[groupIndex]) {
      const currentActiveTab = activeProductTabs[groupIndex] ?? 0;
      // Calculate length from the actual post-removal array (not stale state)
      const newProducts = [...currentGroups[groupIndex].products];
      newProducts.splice(productIndex, 1);
      const newProductsLength = newProducts.length;

      // If we're removing the currently selected tab
      if (currentActiveTab === productIndex) {
        // Select the tab to the right if it exists, otherwise select the one to the left
        const newActiveTab =
          productIndex < newProductsLength ? productIndex : Math.max(0, productIndex - 1);
        setActiveProductTabs({
          ...activeProductTabs,
          [groupIndex]: newActiveTab,
        });
      }
      // If we're removing a tab to the left of the current tab
      else if (productIndex < currentActiveTab) {
        // Shift the current tab index down by 1
        setActiveProductTabs({
          ...activeProductTabs,
          [groupIndex]: currentActiveTab - 1,
        });
      }
      // If we're removing a tab to the right of the current tab, no change needed
    }
  };

  const addProductForFrequency = (freq: string, groupIndex: number) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter && groups[groupIndex]) {
      const newGroups = [...groups];
      const newProduct = {
        productID: Math.random().toString(36).substring(2, 9),
        productName: '',
        productPrice: '',
      };
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        products: [...newGroups[groupIndex].products, newProduct],
      };
      setter(newGroups);
      setActiveProductTabs({
        ...activeProductTabs,
        [groupIndex]: newGroups[groupIndex].products.length - 1,
      });
    }
  };

  const removeProductForFrequency = (freq: string, groupIndex: number, productIndex: number) => {
    const setterMap = getProductGroupFrequencySetterMap();
    const optionsMap = getProductGroupFrequencyOptionsMap();
    const groups = optionsMap[freq as keyof typeof optionsMap] as ProductGroup[] | undefined;
    const setter = setterMap[freq as keyof typeof setterMap];

    if (groups && setter && groups[groupIndex] && groups[groupIndex].products[productIndex]) {
      const newGroups = [...groups];
      const newProducts = [...newGroups[groupIndex].products];
      const currentTab = activeProductTabs[groupIndex] ?? 0;

      newProducts.splice(productIndex, 1);
      const newProductsLength = newProducts.length; // Length after removal
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        products: newProducts,
      };
      setter(newGroups);

      // If we're removing the currently selected tab
      if (currentTab === productIndex) {
        // Select the tab to the right if it exists, otherwise select the one to the left
        const newActiveTab =
          productIndex < newProductsLength ? productIndex : Math.max(0, productIndex - 1);
        setActiveProductTabs({
          ...activeProductTabs,
          [groupIndex]: newActiveTab,
        });
      }
      // If we're removing a tab to the left of the current tab
      else if (productIndex < currentTab) {
        // Shift the current tab index down by 1
        setActiveProductTabs({
          ...activeProductTabs,
          [groupIndex]: currentTab - 1,
        });
      }
      // If we're removing a tab to the right of the current tab, no change needed
    }
  };

  // Legacy Product Groups Handlers removed - now using per-frequency helpers

  // Track active product tab for each group (for Sponsorships)
  const [activeProductTabs, setActiveProductTabs] = useState<{ [key: number]: number }>({});

  const isLastStep = () => {
    const steps = Object.values(FORM_STEPS);
    return currentStep === steps[steps.length - 1];
  };

  // Step validation with invalidFields and toast
  const validateCurrentStep = () => {
    const currentStepInvalidFields = new Set<string>();
    const errorMessages: string[] = [];

    switch (currentStep) {
      case FORM_STEPS.APPEARANCE:
        if (!fundraiserName.trim()) {
          currentStepInvalidFields.add('form_configuration-appearance-fundraiserName');
          errorMessages.push('Fundraiser Name is required');
        }
        if (funds.length === 0) {
          currentStepInvalidFields.add('form_configuration-appearance-funds');
          errorMessages.push('At least one Fund is required');
        }
        if (!color || color.length !== 7 || !color.startsWith('#')) {
          currentStepInvalidFields.add('form_configuration-appearance-color');
          errorMessages.push('Color is required');
        }
        if (showFundraisingBar && (targetAmount === '' || parseInt(targetAmount) <= 0)) {
          currentStepInvalidFields.add('form_configuration-appearance-targetAmount');
          errorMessages.push('A target amount greater than 0 is required');
        }
        if (showFundraisingBar && trackingMetric === 'PRODUCT_COUNT' && formType !== 'PRODUCT') {
          currentStepInvalidFields.add('form_configuration-appearance-trackingMetric');
          errorMessages.push('"Items" tracking requires a Sponsorship amount type');
        }
        break;

      case FORM_STEPS.FREQUENCY:
        if (checkedValues.length === 0) {
          currentStepInvalidFields.add('form_configuration-frequency-frequencies');
          errorMessages.push('At least one frequency must be enabled');
        }
        if (!defaultDonationType || defaultDonationType === '') {
          currentStepInvalidFields.add('form_configuration-frequency-defaultFrequency');
          errorMessages.push('Default Frequency is required');
        }
        // Validate schedule date configuration for each frequency
        if (requireScheduleDonations && frequencyScheduledRanges) {
          const recurringFrequencies: RecurringFrequency[] = [
            'Daily',
            'Weekly',
            'Monthly',
            'Annually',
          ];
          for (const freq of recurringFrequencies) {
            const error = getScheduleValidationError(freq);
            if (error) {
              const validationMessage = getScheduleValidationMessage(freq);
              // Add specific field names for targeted red border styling
              if (error === 'start_date_missing' || error === 'start_after_end') {
                currentStepInvalidFields.add(`form_configuration-frequency-${freq}-startDate`);
              }
              if (error === 'end_date_missing' || error === 'start_after_end') {
                currentStepInvalidFields.add(`form_configuration-frequency-${freq}-endDate`);
              }
              // Also add generic field for error message display
              currentStepInvalidFields.add(
                `form_configuration-frequency-${freq}-scheduleOffsetValidation`,
              );
              errorMessages.push(`${freq}: ${validationMessage}`);
            }
          }
        }
        break;

      case FORM_STEPS.AMOUNTS: {
        // Validate that a valid Amount Button type is selected
        const validFormTypes = ['EMBED_FORM', 'CROWDFUNDING_FORM', 'PRODUCT'];
        if (!formType || !validFormTypes.includes(formType)) {
          currentStepInvalidFields.add('form_configuration-amounts-formType');
          errorMessages.push('Please select an Amount Button type');
        }

        // Validate based on the selected form type
        if (formType === 'EMBED_FORM') {
          // Validate standard frequency amounts - each amount field individually
          const frequencies: (keyof FrequencyAmountsMap)[] = [
            'One-Time',
            'Daily',
            'Weekly',
            'Monthly',
            'Annually',
          ];
          frequencies.forEach((freq) => {
            if (checkedValues.includes(freq)) {
              const index = checkedValues.indexOf(freq);
              const amounts = frequencyAmounts?.[freq];
              if (amounts) {
                // Validate that ALL four amounts are filled (each one is required)
                const amountFields = [
                  { key: 'amount1', value: amounts.amount1, label: 'Amount 1' },
                  { key: 'amount2', value: amounts.amount2, label: 'Amount 2' },
                  { key: 'amount3', value: amounts.amount3, label: 'Amount 3' },
                  { key: 'amount4', value: amounts.amount4, label: 'Amount 4' },
                ];

                amountFields.forEach((field) => {
                  if (!field.value || !field.value.trim()) {
                    const fieldName = getValidationFieldName(freq, index, field.key);
                    currentStepInvalidFields.add(fieldName);
                    errorMessages.push(`${freq} ${field.label} is required`);
                  } else {
                    // Validate that amount is between $1 and $1,000,000
                    const amountValue = parseFloat(field.value);
                    if (isNaN(amountValue) || amountValue < 1) {
                      const fieldName = getValidationFieldName(freq, index, field.key);
                      currentStepInvalidFields.add(fieldName);
                      errorMessages.push(`${freq} ${field.label} must be at least $1`);
                    } else if (amountValue > 1000000) {
                      const fieldName = getValidationFieldName(freq, index, field.key);
                      currentStepInvalidFields.add(fieldName);
                      errorMessages.push(`${freq} ${field.label} cannot exceed $1,000,000`);
                    }
                  }
                });

                // Validate default amount (optional field)
                if (amounts.defaultAmount && amounts.defaultAmount.trim()) {
                  const defaultAmountValue = parseFloat(amounts.defaultAmount);
                  if (isNaN(defaultAmountValue) || defaultAmountValue < 1) {
                    currentStepInvalidFields.add(
                      `form_configuration-amounts-${freq
                        .toLowerCase()
                        .replace('-', '')}-${index}-defaultAmount`,
                    );
                    errorMessages.push(`${freq} Default Suggested Amount must be at least $1`);
                  } else if (defaultAmountValue > 1000000) {
                    currentStepInvalidFields.add(
                      `form_configuration-amounts-${freq
                        .toLowerCase()
                        .replace('-', '')}-${index}-defaultAmount`,
                    );
                    errorMessages.push(`${freq} Default Suggested Amount cannot exceed $1,000,000`);
                  }
                }
              }
            }
          });
        } else if (formType === 'CROWDFUNDING_FORM') {
          // NEW: Validate per-frequency giving levels
          const enabledFrequencies = checkedValues.filter((freq) => freq !== '');
          const optionsMap = getFrequencyOptionsMap();

          for (const freq of enabledFrequencies) {
            const freqOptions = (optionsMap[freq as keyof typeof optionsMap] ||
              []) as CrowdfundingOption[];

            // Validate that this frequency has at least one giving level
            if (freqOptions.length === 0) {
              const fieldName = `form_configuration-amounts-crowdfunding-${freq}-missing`;
              currentStepInvalidFields.add(fieldName);
              errorMessages.push(`${freq}: At least one Giving Level is required`);
              continue; // Skip to next frequency
            }

            // Validate each giving level for this frequency
            freqOptions.forEach((level, index) => {
              if (!level.title || !level.title.trim()) {
                const fieldName = `form_configuration-amounts-crowdfunding-${freq}-${index}-title`;
                currentStepInvalidFields.add(fieldName);
                errorMessages.push(`${freq} - Giving Level ${index + 1}: Title is required`);
              }

              if (!level.amount || !level.amount.trim()) {
                const fieldName = `form_configuration-amounts-crowdfunding-${freq}-${index}-amount`;
                currentStepInvalidFields.add(fieldName);
                errorMessages.push(`${freq} - Giving Level ${index + 1}: Amount is required`);
              } else {
                const amountValue = parseFloat(level.amount);
                if (isNaN(amountValue) || amountValue < 1) {
                  const fieldName = `form_configuration-amounts-crowdfunding-${freq}-${index}-amount`;
                  currentStepInvalidFields.add(fieldName);
                  errorMessages.push(
                    `${freq} - Giving Level ${index + 1}: Amount must be at least $1`,
                  );
                } else if (amountValue > 1000000) {
                  const fieldName = `form_configuration-amounts-crowdfunding-${freq}-${index}-amount`;
                  currentStepInvalidFields.add(fieldName);
                  errorMessages.push(
                    `${freq} - Giving Level ${index + 1}: Amount cannot exceed $1,000,000`,
                  );
                }
              }

              if (!level.description || !level.description.trim()) {
                const fieldName = `form_configuration-amounts-crowdfunding-${freq}-${index}-description`;
                currentStepInvalidFields.add(fieldName);
                errorMessages.push(`${freq} - Giving Level ${index + 1}: Description is required`);
              }
            });
          }

          // Validate default suggested amount (optional field)
          if (
            defaultDonationType &&
            frequencyAmounts &&
            frequencyAmounts[defaultDonationType as keyof FrequencyAmountsMap]
          ) {
            const defaultAmountValue =
              frequencyAmounts[defaultDonationType as keyof FrequencyAmountsMap].defaultAmount;
            if (defaultAmountValue && defaultAmountValue.trim()) {
              const parsedDefaultAmount = parseFloat(defaultAmountValue);
              if (isNaN(parsedDefaultAmount) || parsedDefaultAmount < 1) {
                currentStepInvalidFields.add(
                  'form_configuration-amounts-crowdfunding-defaultAmount',
                );
                errorMessages.push('Default Suggested Amount must be at least $1');
              } else if (parsedDefaultAmount > 1000000) {
                currentStepInvalidFields.add(
                  'form_configuration-amounts-crowdfunding-defaultAmount',
                );
                errorMessages.push('Default Suggested Amount cannot exceed $1,000,000');
              }
            }
          }
        } else if (formType === 'PRODUCT') {
          // Reconcile all non-active frequency tabs before validation.
          // When a user adds/edits sponsorship groups on one tab, the data only
          // propagates to other tabs via reconciliation (triggered on tab click).
          // Without this pre-validation reconciliation, validation flags empty fields
          // on tabs the user never visited even though the data exists on other tabs.
          //
          // We must reconcile AND re-read in one pass because React state setters are
          // async — calling the setter then reading getProductGroupFrequencyOptionsMap()
          // would still see stale state. So we build a reconciled snapshot in memory.
          const enabledFrequencies = checkedValues.filter((freq) => freq !== '');
          const rawOptionsMap = getProductGroupFrequencyOptionsMap();

          // Build a reconciled copy of all frequency product groups
          const reconciledMap: Record<string, ProductGroup[]> = {};
          for (const freq of enabledFrequencies) {
            const targetGroups = [
              ...((rawOptionsMap[freq as keyof typeof rawOptionsMap] || []) as ProductGroup[]),
            ];
            const sourceFrequencies = frequencyOrder.filter(
              (f) => f !== freq && enabledFrequencies.includes(f),
            );
            const maxGroupCount = Math.max(
              targetGroups.length,
              ...sourceFrequencies.map(
                (f) =>
                  ((rawOptionsMap[f as keyof typeof rawOptionsMap] || []) as ProductGroup[]).length,
              ),
            );

            for (let gi = 0; gi < maxGroupCount; gi += 1) {
              const sourceGroup = sourceFrequencies
                .map(
                  (f) =>
                    ((rawOptionsMap[f as keyof typeof rawOptionsMap] || []) as ProductGroup[])[gi],
                )
                .find((g) => hasProductGroupData(g));
              if (!sourceGroup) continue;

              if (!targetGroups[gi]) {
                targetGroups[gi] = cloneProductGroup(sourceGroup);
                continue;
              }

              let nextGroup = targetGroups[gi];
              if (
                !hasTextValue(nextGroup.productGroupName) &&
                hasTextValue(sourceGroup.productGroupName)
              ) {
                nextGroup = { ...nextGroup, productGroupName: sourceGroup.productGroupName };
              }
              if (
                !hasTextValue(nextGroup.productGroupDescription) &&
                hasTextValue(sourceGroup.productGroupDescription)
              ) {
                nextGroup = {
                  ...nextGroup,
                  productGroupDescription: sourceGroup.productGroupDescription,
                };
              }

              const maxProductCount = Math.max(
                nextGroup.products.length,
                ...sourceFrequencies.map(
                  (f) =>
                    ((rawOptionsMap[f as keyof typeof rawOptionsMap] || []) as ProductGroup[])[gi]
                      ?.products.length || 0,
                ),
              );
              const nextProducts = [...nextGroup.products];
              for (let pi = 0; pi < maxProductCount; pi += 1) {
                const sourceProduct = sourceFrequencies
                  .map(
                    (f) =>
                      ((rawOptionsMap[f as keyof typeof rawOptionsMap] || []) as ProductGroup[])[gi]
                        ?.products[pi],
                  )
                  .find((p) => hasProductData(p));
                if (!sourceProduct) continue;

                if (!nextProducts[pi]) {
                  nextProducts[pi] = cloneProductOption(sourceProduct);
                  continue;
                }
                let nextProduct = nextProducts[pi];
                if (
                  !hasTextValue(nextProduct.productName) &&
                  hasTextValue(sourceProduct.productName)
                ) {
                  nextProduct = { ...nextProduct, productName: sourceProduct.productName };
                }
                if (
                  !hasTextValue(nextProduct.productPrice) &&
                  hasTextValue(sourceProduct.productPrice)
                ) {
                  nextProduct = { ...nextProduct, productPrice: sourceProduct.productPrice };
                }
                nextProducts[pi] = nextProduct;
              }
              nextGroup = { ...nextGroup, products: nextProducts };
              targetGroups[gi] = nextGroup;
            }
            reconciledMap[freq] = targetGroups;
          }

          // Also persist the reconciled data to state so tabs reflect the updated values
          const setterMap = getProductGroupFrequencySetterMap();
          for (const freq of enabledFrequencies) {
            const setter = setterMap[freq as keyof typeof setterMap];
            const rawGroups = (rawOptionsMap[freq as keyof typeof rawOptionsMap] ||
              []) as ProductGroup[];
            if (setter && JSON.stringify(rawGroups) !== JSON.stringify(reconciledMap[freq])) {
              setter(reconciledMap[freq]);
              clearSponsorshipValidationErrorsForFrequency(freq);
            }
          }

          // Use reconciledMap for validation instead of raw state
          const productGroupOptionsMap = reconciledMap;

          for (const freq of enabledFrequencies) {
            const freqGroups = (productGroupOptionsMap[
              freq as keyof typeof productGroupOptionsMap
            ] || []) as ProductGroup[];

            if (freqGroups.length === 0) {
              const fieldName = `form_configuration-amounts-sponsorship-${freq}-missing`;
              currentStepInvalidFields.add(fieldName);
              errorMessages.push(`${freq}: At least one Sponsorship Group is required`);
              continue;
            }

            freqGroups.forEach((group, groupIndex) => {
              if (!group.productGroupName || !group.productGroupName.trim()) {
                const fieldName = `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-name`;
                currentStepInvalidFields.add(fieldName);
                errorMessages.push(
                  `${freq} - Sponsorship Group ${groupIndex + 1}: Group Title is required`,
                );
              }

              if (!group.products || group.products.length === 0) {
                const fieldName = `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-products`;
                currentStepInvalidFields.add(fieldName);
                errorMessages.push(
                  `${freq} - Sponsorship Group ${groupIndex + 1}: At least one item is required`,
                );
              } else {
                group.products.forEach((product, productIndex) => {
                  if (!product.productName || !product.productName.trim()) {
                    const fieldName = `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-name`;
                    currentStepInvalidFields.add(fieldName);
                    errorMessages.push(
                      `${freq} - Sponsorship Group ${groupIndex + 1}, Item ${
                        productIndex + 1
                      }: Title is required`,
                    );
                  }

                  if (!product.productPrice || !product.productPrice.trim()) {
                    const fieldName = `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-price`;
                    currentStepInvalidFields.add(fieldName);
                    errorMessages.push(
                      `${freq} - Sponsorship Group ${groupIndex + 1}, Item ${
                        productIndex + 1
                      }: Amount is required`,
                    );
                  } else {
                    const priceValue = parseFloat(product.productPrice);
                    if (isNaN(priceValue) || priceValue < 1) {
                      const fieldName = `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-price`;
                      currentStepInvalidFields.add(fieldName);
                      errorMessages.push(
                        `${freq} - Sponsorship Group ${groupIndex + 1}, Item ${
                          productIndex + 1
                        }: Amount must be at least $1`,
                      );
                    } else if (priceValue > 1000000) {
                      const fieldName = `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-price`;
                      currentStepInvalidFields.add(fieldName);
                      errorMessages.push(
                        `${freq} - Sponsorship Group ${groupIndex + 1}, Item ${
                          productIndex + 1
                        }: Amount cannot exceed $1,000,000`,
                      );
                    }
                  }
                });
              }
            });
          }

          // Optional: Validate that product group names match across frequencies (RECOMMENDED)
          const firstFreq = enabledFrequencies[0];
          if (firstFreq && enabledFrequencies.length > 1) {
            const firstFreqGroups = (productGroupOptionsMap[
              firstFreq as keyof typeof productGroupOptionsMap
            ] || []) as ProductGroup[];
            const firstFreqGroupNames = firstFreqGroups.map((g) => g.productGroupName).sort();

            for (let i = 1; i < enabledFrequencies.length; i++) {
              const freq = enabledFrequencies[i];
              const freqGroups = (productGroupOptionsMap[
                freq as keyof typeof productGroupOptionsMap
              ] || []) as ProductGroup[];
              const freqGroupNames = freqGroups.map((g) => g.productGroupName).sort();

              if (JSON.stringify(firstFreqGroupNames) !== JSON.stringify(freqGroupNames)) {
                currentStepInvalidFields.add(`form_configuration-amounts-sponsorship-nameMismatch`);
                errorMessages.push(
                  `Sponsorship Group names must match across all frequencies. ` +
                    `${firstFreq} has: [${firstFreqGroupNames.join(', ')}], ` +
                    `${freq} has: [${freqGroupNames.join(', ')}]`,
                );
                break;
              }
            }
          }
        }
        break;
      }

      case FORM_STEPS.CHECKOUT:
        // Validate dedication types when dedication is enabled
        if (enableDedication && dedicationTypes.length === 0) {
          currentStepInvalidFields.add('form_configuration-checkout-dedicationTypes');
          errorMessages.push(
            'At least one Dedication Type must be selected when Dedication is enabled',
          );
        }
        break;

      default:
        break;
    }

    // Log validation summary

    // Get current step field prefixes to identify which fields belong to this step
    const getCurrentStepFieldPrefixes = () => {
      switch (currentStep) {
        case FORM_STEPS.APPEARANCE:
          return ['form_configuration-appearance-'];
        case FORM_STEPS.FREQUENCY:
          return ['form_configuration-frequency-'];
        case FORM_STEPS.AMOUNTS:
          return ['form_configuration-amounts-'];
        case FORM_STEPS.CHECKOUT:
          return ['form_configuration-checkout-'];
        default:
          return [];
      }
    };

    // Update invalid fields state by merging with existing errors from other tabs
    setInvalidFields((prevInvalidFields) => {
      const newInvalidFields = new Set(prevInvalidFields);
      const currentStepPrefixes = getCurrentStepFieldPrefixes();

      // Remove existing errors for the current step
      for (const field of newInvalidFields) {
        const shouldRemove = currentStepPrefixes.some(
          (prefix) => field === prefix || field.startsWith(prefix),
        );
        if (shouldRemove) {
          newInvalidFields.delete(field);
        }
      }

      // Add new errors for the current step
      currentStepInvalidFields.forEach((field) => {
        newInvalidFields.add(field);
      });

      return newInvalidFields;
    });

    // Show toast with errors if any
    if (errorMessages.length > 0) {
      toast.error(getValidationToastMessage(errorMessages, 'Please fix the validation errors'));
      return false;
    }

    return true;
  };

  // Helper function to check if a step has validation errors
  const getStepValidationErrors = (stepKey: string): boolean => {
    // Check if any field in invalidFields contains this step's identifier
    const stepIdentifier = `form_configuration-${stepKey}-`;
    return Array.from(invalidFields).some((field) => field.startsWith(stepIdentifier));
  };

  // Handle frequency checkbox change
  const handleFrequencyCheckboxChange = (freq: string, checked: boolean) => {
    if (checked) {
      const newCheckedValues = [...checkedValues, freq];
      setCheckedValues(newCheckedValues);
      // Clear frequency validation error when adding a frequency
      handleFieldFocus('form_configuration-frequency-frequencies');

      // Handle Default Frequency behavior based on number of checked frequencies
      if (newCheckedValues.length === 1) {
        // Auto-set to the only checked frequency
        setDefaultDonationType(freq);
      } else if (newCheckedValues.length === 2) {
        // Reset default frequency when going from 1 to 2 checked values
        setDefaultDonationType('');
      }

      // Set default amounts for the newly checked frequency
      if (setFrequencyAmounts && frequencyAmounts && formType === 'EMBED_FORM') {
        reconcileStandardFrequencyFromExistingData(freq);
      }

      if (formType === 'CROWDFUNDING_FORM') {
        reconcileCrowdfundingFrequencyFromExistingData(freq);
      }

      if (formType === 'PRODUCT') {
        reconcileSponsorshipFrequencyFromExistingData(freq);
      }
    } else {
      const newCheckedValues = checkedValues.filter((f) => f !== freq);
      setCheckedValues(newCheckedValues);

      // Handle Default Frequency behavior when unchecking
      if (newCheckedValues.length === 1) {
        // Auto-set to the only remaining frequency
        setDefaultDonationType(newCheckedValues[0]);
      } else if (newCheckedValues.length === 0) {
        // Clear default frequency when no frequencies are checked
        setDefaultDonationType('');
      } else if (defaultDonationType === freq) {
        // If we unchecked the current default, reset it
        setDefaultDonationType('');
      }

      // Clear all amount validation errors for this frequency (regardless of index)
      const normalizedFreq = freq.toLowerCase().replace('-', '');
      setInvalidFields((prevInvalidFields) => {
        const newInvalidFields = new Set(prevInvalidFields);
        // Remove validation errors that match this frequency pattern
        // This handles index shifts when frequencies are removed
        for (const field of newInvalidFields) {
          if (field.startsWith(`form_configuration-amounts-${normalizedFreq}-`)) {
            newInvalidFields.delete(field);
          }
        }
        return newInvalidFields;
      });

      // NOTE: We intentionally do NOT reset frequencyAmounts[freq] to empty values here.
      // The UI only renders tabs for frequencies in checkedValues, so disabled frequencies
      // won't be displayed. Preserving the data allows users to re-enable a frequency
      // without losing their configured amounts (better UX for accidental toggles).
    }
  };

  const renderAllFormSteps = () => {
    const steps = [
      {
        key: FORM_STEPS.APPEARANCE,
        title: 'Appearance',
        content: (
          <div className="space-y-3">
            {/* Title — Driver.js onboarding step target (Phase 1 Spike 1) */}
            <div>
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Title
              </Label>
              <FormInput
                type="text"
                value={fundraiserName}
                onChange={(e) => setFundraiserName(e.target.value)}
                onFocus={() => handleFieldFocus('form_configuration-appearance-fundraiserName')}
                className={`h-7 ${
                  isFieldInvalid('form_configuration-appearance-fundraiserName')
                    ? 'border-red-500'
                    : ''
                }`}
              />
            </div>

            {/* Fund Selection */}
            <div className="space-y-1">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Funds
              </Label>
              <StyledMultiSelectDropdown
                options={
                  selectableFunds && selectableFunds.length > 0
                    ? selectableFunds.map((fund) => {
                        if (typeof fund === 'string') {
                          return { value: fund, label: fund };
                        }
                        return {
                          value: fund.value || fund.label || '',
                          label: fund.label || fund.value || '',
                        };
                      })
                    : []
                }
                items={funds || []}
                setItems={(newFunds: string[]) => {
                  setFunds(newFunds);
                  // Clear validation error when user makes selection
                  if (newFunds.length > 0) {
                    handleFieldFocus('form_configuration-appearance-funds');
                  }
                }}
                placeholder="Select or type to create funds"
                backgroundColor="#FFFFFF"
                className={
                  isFieldInvalid('form_configuration-appearance-funds') ? 'border-red-500' : ''
                }
              />
            </div>

            {/* Field Mapping Section - Regular integrations (exclude ICNA Relief) */}
            {funds &&
              funds.length > 0 &&
              integrationType !== null &&
              merchantName !== 'ICNA Relief USA' &&
              merchantName !== 'ICNA Relief USA Dev' && (
                <div className="mr-4 mt-2 flex">
                  <button
                    type="button"
                    className="flex cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowFieldMappingModal(true);
                    }}
                  >
                    <PlusIcon className="mr-1 mt-0.5 h-5 w-5 text-ink-muted" aria-hidden="true" />
                    <div className="text-ink-muted text-sm"> Map Field Values </div>
                    {fieldMappings.length > 0 && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-sage-light px-2 py-0.5 text-xs font-medium text-sage">
                        {fieldMappings.length} mapped
                      </span>
                    )}
                  </button>
                </div>
              )}

            {/* Field Mapping Section - ICNA Relief Salesforce */}
            {funds &&
              funds.length > 0 &&
              (merchantName === 'ICNA Relief USA' || merchantName === 'ICNA Relief USA Dev') &&
              setSalesforceFieldMapperOpen &&
              salesforceCampaignIDICNAValues !== undefined && (
                <div className="mr-4 mt-2 flex">
                  <button
                    type="button"
                    className="flex cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setSalesforceFieldMapperOpen(true);
                    }}
                  >
                    <PlusIcon className="mr-1 mt-0.5 h-5 w-5 text-ink-muted" aria-hidden="true" />
                    <div className="text-ink-muted text-md">
                      {salesforceCampaignIDICNAValues
                        ? 'Edit Campaign ID Mapping'
                        : 'Map Campaign ID'}
                    </div>
                  </button>
                </div>
              )}

            {/* Display ICNA Relief Salesforce Mapping */}
            {salesforceCampaignIDICNAValues && (
              <div className="flex flex-wrap gap-2">
                <div className="relative flex border border-edge p-1 px-2 rounded-md w-fit flex-col gap-1 shadow-md">
                  <Label className="block font-inter text-md font-medium leading-5 text-ink-muted">
                    {fundraiserName || 'Fundraiser'}
                  </Label>
                  <div className="flex items-center gap-1">
                    <span className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                      Salesforce Campaign ID
                    </span>
                    <span className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                      :
                    </span>
                    <span className="block font-inter text-xs font-medium leading-5 text-ink-muted">
                      {salesforceCampaignIDICNAValues}
                    </span>
                  </div>
                  <div className="absolute top-0 right-0">
                    <button
                      type="button"
                      onClick={() => setSalesforceCampaignIDICNAValues?.('')}
                      className="hover:opacity-70"
                    >
                      <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                        <path
                          d="M4 4L12 12M4 12L12 4"
                          stroke="#3A3429"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="space-y-1">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Description
              </Label>
              {editorState && setEditorState ? (
                <Editor
                  editorStyle={{
                    outline: 'none',
                    borderRadius: 5,
                    backgroundColor: '#FFFFFF',
                    paddingLeft: 4,
                    paddingRight: 4,
                    minHeight: 200,
                    paddingTop: 2,
                    paddingBottom: 2,
                  }}
                  toolbarStyle={{
                    borderRadius: 5,
                    backgroundColor: '#F0ECE4',
                  }}
                  wrapperStyle={{ borderRadius: 5, overflow: 'hidden' }}
                  editorState={editorState}
                  onEditorStateChange={setEditorState}
                  handlePastedText={handlePastedText}
                  wrapperClassName="wrapper-class fundraiser-description-editor h-100 border border-edge bg-white"
                  editorClassName="editor-class h-100 px-4 py-0"
                  toolbarClassName="toolbar-class"
                  toolbar={{
                    options: ['link', 'list'],
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered'],
                    },
                    link: {
                      inDropdown: false,
                      showOpenOptionOnHover: true,
                      defaultTargetOption: '_blank',
                    },
                  }}
                />
              ) : (
                // Fallback to textarea if editor props not available
                <FormTextarea
                  rows={8}
                  className="resize-none"
                  placeholder="Enter your fundraiser description..."
                  value={description || ''}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2500}
                />
              )}
              {editorState && (
                <p
                  className={`text-xs text-right mt-1 ${
                    editorState.getCurrentContent().getPlainText('').length > 2500
                      ? 'text-red-500 font-medium'
                      : 'text-ink-muted'
                  }`}
                >
                  {editorState.getCurrentContent().getPlainText('').length}/2500 characters
                </p>
              )}
            </div>

            {/* Header Image */}
            <div className="space-y-1">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Header Image
              </Label>
              <ImageUploader
                merchantName={merchantName}
                elementID={elementID}
                image_type={'fundraiser'}
                desiredHeight={0}
                desiredWidth={0}
                image={image}
                setImage={setImage}
                setResizedImage={setResizedImage}
              />
            </div>

            {/* Color — Driver.js onboarding step target (Phase 1 Spike 1) */}
            <div className="space-y-1">
              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                Color
              </Label>
              <FormColorSelector
                value={color}
                onChange={setColor}
                defaultColor={COLORS.FORM_DEFAULT_COLOR}
                className="h-7"
                isInvalid={isFieldInvalid('form_configuration-appearance-color')}
                onFocus={() => handleFieldFocus('form_configuration-appearance-color')}
              />
            </div>

            {/* Elements */}
            <div className="space-y-2 pt-6">
              <Label className="text-base font-medium text-ink-secondary">
                Elements Customization
              </Label>

              <div>
                {/* Details Page */}
                <div className="py-3">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableEmbedForm}
                      onCheckedChange={(value) => setEnableEmbedForm(value)}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Details Page
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Shows description, FAQs, pictures, and more
                      </p>
                    </div>
                  </div>
                </div>

                {/* Fundraiser Bar */}
                <div className="border-t border-edge py-3">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={showFundraisingBar}
                      onCheckedChange={(value) => setShowFundraisingBar(value)}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Fundraiser Bar
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Progress bar showing goal and amount raised
                      </p>
                    </div>
                  </div>

                  {showFundraisingBar && (
                    <div className="mt-2 space-y-2 rounded-md border border-edge bg-paper-light p-2 [&_label]:text-ink-secondary [&_p.text-xs]:text-ink-label">
                      {/* Tracking Metric — 2-Button Segmented Control */}
                      <div className="flex rounded-md border border-edge bg-paper-inset p-0.5">
                        <button
                          type="button"
                          onClick={() => {
                            setTrackingMetric('DOLLAR_AMOUNT');
                            setCountOffset('0');
                          }}
                          className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
                            trackingMetric === 'DOLLAR_AMOUNT' || !trackingMetric
                              ? 'border-edge bg-paper-light text-ink-secondary'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary'
                          }`}
                        >
                          Dollar Amount
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (trackingMetric === 'DOLLAR_AMOUNT' || !trackingMetric) {
                              setTrackingMetric('SUBSCRIPTION_COUNT');
                            }
                            setAmountOffset('0');
                          }}
                          className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
                            trackingMetric !== 'DOLLAR_AMOUNT' && trackingMetric
                              ? 'border-edge bg-paper-light text-ink-secondary'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary'
                          }`}
                        >
                          Count
                        </button>
                      </div>

                      {/* Conditional inputs based on tracking metric */}
                      {trackingMetric !== 'DOLLAR_AMOUNT' && trackingMetric ? (
                        <>
                          {/* Count Type — Radio Sub-Selector */}
                          <div className="flex flex-col sm:flex-row py-2 gap-1 sm:gap-8">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="count-type"
                                value="SUBSCRIPTION_COUNT"
                                className="h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage border-ink-secondary text-sage"
                                checked={trackingMetric === 'SUBSCRIPTION_COUNT'}
                                onChange={() => setTrackingMetric('SUBSCRIPTION_COUNT')}
                              />
                              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                Subscriptions
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="count-type"
                                value="DONATION_COUNT"
                                className="h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage border-ink-secondary text-sage"
                                checked={trackingMetric === 'DONATION_COUNT'}
                                onChange={() => setTrackingMetric('DONATION_COUNT')}
                              />
                              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                Donations
                              </Label>
                            </div>
                            <div
                              className={`flex items-center space-x-2 ${formType !== 'PRODUCT' ? 'opacity-40 cursor-not-allowed' : ''}`}
                            >
                              <input
                                type="radio"
                                name="count-type"
                                value="PRODUCT_COUNT"
                                className="h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage border-ink-secondary text-sage"
                                checked={trackingMetric === 'PRODUCT_COUNT'}
                                disabled={formType !== 'PRODUCT'}
                                onChange={() => {
                                  if (formType === 'PRODUCT') setTrackingMetric('PRODUCT_COUNT');
                                }}
                              />
                              <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                Items
                                {formType !== 'PRODUCT' && (
                                  <span className="ml-1 text-[11px] font-normal text-ink-muted">
                                    (Sponsorship forms only)
                                  </span>
                                )}
                              </Label>
                            </div>
                          </div>

                          {/* Count Config Fields */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="block font-inter text-sm font-medium leading-5">
                                {trackingMetric === 'PRODUCT_COUNT'
                                  ? 'Items Goal'
                                  : trackingMetric === 'DONATION_COUNT'
                                    ? 'Donations Goal'
                                    : 'Subscriptions Goal'}
                              </Label>
                              <FormInput
                                type="text"
                                inputMode="numeric"
                                value={formatWithCommas(targetAmount)}
                                onChange={(e) => setTargetAmount(stripNonNumeric(e.target.value))}
                                onFocus={() =>
                                  handleFieldFocus('form_configuration-appearance-targetAmount')
                                }
                                className={`h-7 ${
                                  isFieldInvalid('form_configuration-appearance-targetAmount')
                                    ? 'border-red-500'
                                    : ''
                                }`}
                                placeholder="100"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="block font-inter text-sm font-medium leading-5">
                                Count Offset
                              </Label>
                              <FormInput
                                type="text"
                                inputMode="numeric"
                                className="h-7"
                                value={formatWithCommas(countOffset || '')}
                                onChange={(e) => setCountOffset(stripNonNumeric(e.target.value))}
                              />
                            </div>
                            <div className="space-y-1 sm:col-span-2">
                              <Label className="block font-inter text-sm font-medium leading-5">
                                Display Label
                              </Label>
                              <FormInput
                                type="text"
                                className="h-7"
                                value={metricLabel}
                                onChange={(e) => {
                                  if (e.target.value.length <= 18) {
                                    setMetricLabel(e.target.value);
                                  }
                                }}
                                placeholder={
                                  trackingMetric === 'PRODUCT_COUNT'
                                    ? 'Items'
                                    : trackingMetric === 'DONATION_COUNT'
                                      ? 'Payments'
                                      : 'Sustainers'
                                }
                                maxLength={18}
                              />
                              <p className="mt-0.5 text-xs">
                                {trackingMetric === 'PRODUCT_COUNT'
                                  ? "The word shown after the count (e.g. '42 Items')"
                                  : trackingMetric === 'DONATION_COUNT'
                                    ? "The word shown after the count (e.g. '312 Donations')"
                                    : "The word shown after the count (e.g. '312 Sustainers')"}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="block font-inter text-sm font-medium leading-5">
                              Goal
                            </Label>
                            <FormInput
                              type="text"
                              inputMode="numeric"
                              value={targetAmount ? `$${formatWithCommas(targetAmount)}` : ''}
                              onChange={(e) => setTargetAmount(stripNonNumeric(e.target.value))}
                              onFocus={() =>
                                handleFieldFocus('form_configuration-appearance-targetAmount')
                              }
                              className={`h-7 ${
                                isFieldInvalid('form_configuration-appearance-targetAmount')
                                  ? 'border-red-500'
                                  : ''
                              }`}
                              placeholder="$3,000"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="block font-inter text-sm font-medium leading-5">
                              Amount Offset
                            </Label>
                            <FormInput
                              type="text"
                              inputMode="numeric"
                              className="h-7"
                              value={amountOffset ? `$${formatWithCommas(amountOffset)}` : ''}
                              onChange={(e) => setAmountOffset(stripNonNumeric(e.target.value))}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Supporter List */}
                <div className="border-t border-edge py-3">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableDonorList}
                      onCheckedChange={(checked) => setEnableDonorList(checked)}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Supporter List
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Display recent or top donors on the form
                      </p>
                    </div>
                  </div>

                  {enableDonorList && (
                    <div className="mt-2 space-y-2 rounded-md border border-edge bg-paper-light p-2 [&_label]:text-ink-secondary">
                      {/* Sort Order — Segmented Control */}
                      <div className="flex rounded-md border border-edge bg-paper-inset p-0.5">
                        <button
                          type="button"
                          onClick={() => setDonorListType('RECENT_DONORS')}
                          className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
                            donorListType === 'RECENT_DONORS'
                              ? 'border-edge bg-paper-light text-ink-secondary'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary'
                          }`}
                        >
                          Recent Donors
                        </button>
                        <button
                          type="button"
                          onClick={() => setDonorListType('TOP_DONORS')}
                          className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
                            donorListType === 'TOP_DONORS'
                              ? 'border-edge bg-paper-light text-ink-secondary'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary'
                          }`}
                        >
                          Top Donors
                        </button>
                      </div>
                      {setShowAllSupportersAnonymous && (
                        <div className="flex items-center gap-2 border-t border-edge pt-2">
                          <Switch
                            checked={!!showAllSupportersAnonymous}
                            onCheckedChange={(checked) => setShowAllSupportersAnonymous(!!checked)}
                          />
                          <Label className="block font-inter text-sm font-medium leading-5">
                            Show all donors as anonymous
                          </Label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        key: FORM_STEPS.FREQUENCY,
        title: 'Frequency',
        content: (
          <div>
            <div className="space-y-6 pb-3">
              {/* Frequency selection group — Enabled + Default Frequency in one
                  form-field-group container (style-guide: bg-paper-light, inner
                  labels ink-secondary). */}
              <div className="space-y-2 rounded-md">
                {/* Enabled Frequencies — Driver.js onboarding step target (Phase 1 Spike 1) */}
                <div className="space-y-2">
                  <Label className="text-ink-secondary">Enabled Frequencies</Label>
                  <div
                    className={`rounded-md border bg-paper-inset p-2 ${
                      isFieldInvalid('form_configuration-frequency-frequencies')
                        ? 'border-red-500'
                        : 'border-edge'
                    }`}
                    onFocus={() => handleFieldFocus('form_configuration-frequency-frequencies')}
                  >
                    <div className="grid grid-cols-3 gap-x-2 gap-y-1">
                      {frequencyOptions.map((freq) => (
                        <Checkbox
                          key={freq}
                          checked={checkedValues.includes(freq)}
                          onChange={(checked) => handleFrequencyCheckboxChange(freq, checked)}
                          label={freq}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Default Frequency - Only show when 2+ frequencies are checked */}
                {checkedValues.length >= 2 && (
                  <div className="space-y-2">
                    <Label htmlFor="default-frequency" className="text-ink-secondary">
                      Default Frequency
                    </Label>
                    <FormSelect
                      id="default-frequency"
                      value={defaultDonationType}
                      onChange={(e) => setDefaultDonationType(e.target.value)}
                      onFocus={() =>
                        handleFieldFocus('form_configuration-frequency-defaultFrequency')
                      }
                      className={`${
                        isFieldInvalid('form_configuration-frequency-defaultFrequency')
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                          : ''
                      }`}
                    >
                      <option value=""></option>
                      {checkedValues.map((freq) => (
                        <option key={freq} value={freq}>
                          {freq}
                        </option>
                      ))}
                    </FormSelect>
                  </div>
                )}
              </div>

              {/* Enable Scheduled Payments - Only show when recurring frequencies are checked */}
              {checkedValues.some((freq) => freq !== 'One-Time') && (
                <div className="space-y-2 ">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={requireScheduleDonations}
                      onCheckedChange={setRequireScheduleDonations}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Scheduled Payments
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Customize time properties for recurring donations
                      </p>
                    </div>
                  </div>

                  {/* Default Scheduled Payment Settings - Per Frequency Configuration */}
                  {requireScheduleDonations && (
                    <>
                      <div className="space-y-2 rounded-md border border-edge bg-paper-light p-2">
                        {/* Frequency Tabs - Filter out 'One-Time' */}
                        <nav
                          className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                          aria-label="Scheduled Payment Frequencies"
                        >
                          {checkedValues
                            .filter((freq) => freq !== 'One-Time')
                            .map((freq, index) => {
                              // Only show tab error styling after validation has been triggered
                              const hasError = isFieldInvalid(
                                `form_configuration-frequency-${freq}-scheduleOffsetValidation`,
                              );

                              return (
                                <button
                                  key={`schedule-${freq}-${index}`}
                                  type="button"
                                  onClick={() => setActiveScheduleTab?.(freq)}
                                  className={getFreqTabClasses(
                                    activeScheduleTab === freq,
                                    hasError,
                                  )}
                                >
                                  {freq}
                                </button>
                              );
                            })}
                        </nav>

                        {/* Tab Content - Configuration for active frequency */}
                        {activeScheduleTab && (
                          <div className="space-y-4 p-2">
                            {/* ── Section 1: Start / End window ── */}
                            <div className="rounded-md border border-edge bg-white px-3 py-5">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={isWindowEnabled(activeScheduleTab as RecurringFrequency)}
                                  onCheckedChange={(checked: boolean) =>
                                    toggleScheduleWindow(
                                      activeScheduleTab as RecurringFrequency,
                                      checked,
                                    )
                                  }
                                />
                                <div>
                                  <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                                    Enable default start / end window
                                  </Label>
                                </div>
                              </div>

                              {isWindowEnabled(activeScheduleTab as RecurringFrequency) && (
                                <div className="mt-3 space-y-2 rounded-md border border-edge bg-paper-inset p-2 [&_label]:text-ink-secondary">
                                  {/* Default Start Date Configuration */}
                                  <div className="space-y-2">
                                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                      Default Start Date
                                    </Label>
                                    <div className="grid grid-cols-2 gap-2">
                                      {/* Date Type Toggle */}
                                      <div className="relative">
                                        <FormSelect
                                          value={
                                            frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.startDateType || 'none'
                                          }
                                          onChange={(e) => {
                                            const newType = e.target.value as DateConfigType;
                                            // Update both fields at once to prevent race conditions
                                            if (newType === 'fixed') {
                                              updateScheduleConfigMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { startDateType: 'fixed', startDateOffset: null },
                                              );
                                            } else if (newType === 'dynamic') {
                                              updateScheduleConfigMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { startDateType: 'dynamic', fixedStartDate: null },
                                              );
                                            } else {
                                              // 'none' - clear both date values
                                              updateScheduleConfigMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                {
                                                  startDateType: 'none',
                                                  startDateOffset: null,
                                                  fixedStartDate: null,
                                                },
                                              );
                                            }
                                          }}
                                          className="h-7 pr-7 leading-7"
                                        >
                                          <option value="none">None</option>
                                          <option value="dynamic">Dynamic</option>
                                          <option value="fixed">Fixed</option>
                                        </FormSelect>
                                        {(frequencyScheduledRanges?.[
                                          activeScheduleTab as RecurringFrequency
                                        ]?.startDateType || 'none') === 'dynamic' && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <QuestionMarkCircleIcon className="absolute right-8 top-1/2 h-4 w-4 -translate-y-1/2 cursor-help text-ink-muted hover:text-ink-secondary" />
                                              </TooltipTrigger>
                                              <TooltipContent side="top">
                                                <p>
                                                  Offsets the Start Date from when the Donor first
                                                  views the form
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>

                                      {/* None or Dynamic: Offset selector (disabled when None, enabled when Dynamic) */}
                                      {((frequencyScheduledRanges?.[
                                        activeScheduleTab as RecurringFrequency
                                      ]?.startDateType || 'none') === 'dynamic' ||
                                        (frequencyScheduledRanges?.[
                                          activeScheduleTab as RecurringFrequency
                                        ]?.startDateType || 'none') === 'none') && (
                                        <FormSelect
                                          value={
                                            (frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.startDateType || 'none') === 'none'
                                              ? ''
                                              : (frequencyScheduledRanges?.[
                                                  activeScheduleTab as RecurringFrequency
                                                ]?.startDateOffset ?? '')
                                          }
                                          onChange={(e) =>
                                            updateScheduleConfigForFrequency(
                                              activeScheduleTab as RecurringFrequency,
                                              'startDateOffset',
                                              e.target.value ? parseInt(e.target.value, 10) : null,
                                            )
                                          }
                                          disabled={
                                            (frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.startDateType || 'none') === 'none'
                                          }
                                          className={`h-7 leading-7 ${
                                            (frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.startDateType || 'none') === 'none'
                                              ? 'bg-paper-inset text-ink-muted cursor-not-allowed'
                                              : ''
                                          } ${
                                            isFieldInvalid(
                                              `form_configuration-frequency-${activeScheduleTab}-startDate`,
                                            )
                                              ? 'border-red-500'
                                              : ''
                                          }`}
                                        >
                                          <option value="">-</option>
                                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                                            <option key={num} value={num}>
                                              In {num}{' '}
                                              {formatTimeUnit(
                                                activeScheduleTab as RecurringFrequency,
                                                num,
                                              )}
                                            </option>
                                          ))}
                                        </FormSelect>
                                      )}

                                      {/* Fixed: DatePicker */}
                                      {frequencyScheduledRanges?.[
                                        activeScheduleTab as RecurringFrequency
                                      ]?.startDateType === 'fixed' && (
                                        <SingleDatePicker
                                          value={
                                            frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.fixedStartDate
                                              ? dayjs(
                                                  frequencyScheduledRanges?.[
                                                    activeScheduleTab as RecurringFrequency
                                                  ]?.fixedStartDate,
                                                )
                                              : null
                                          }
                                          onChange={(date: dayjs.Dayjs | null) =>
                                            updateScheduleConfigForFrequency(
                                              activeScheduleTab as RecurringFrequency,
                                              'fixedStartDate',
                                              date ? date.format('YYYY-MM-DD') : null,
                                            )
                                          }
                                          disablePastDates={true}
                                          placeholder="Select date"
                                          isInvalid={
                                            isFieldInvalid(
                                              `form_configuration-frequency-${activeScheduleTab}-startDate`,
                                            ) ||
                                            (!!frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.fixedStartDate &&
                                              dayjs(
                                                frequencyScheduledRanges?.[
                                                  activeScheduleTab as RecurringFrequency
                                                ]?.fixedStartDate,
                                              ).isBefore(dayjs().startOf('day')))
                                          }
                                          className="h-7 w-full"
                                        />
                                      )}
                                    </div>
                                  </div>

                                  {/* Default End Date Configuration */}
                                  <div className="space-y-2">
                                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                                      Default End Date
                                    </Label>
                                    <div className="grid grid-cols-2 gap-2">
                                      {/* Date Type Toggle */}
                                      <div className="relative">
                                        <FormSelect
                                          value={
                                            frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.endDateType || 'none'
                                          }
                                          onChange={(e) => {
                                            const newType = e.target.value as DateConfigType;
                                            // Update both fields at once to prevent race conditions
                                            if (newType === 'fixed') {
                                              updateScheduleConfigMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { endDateType: 'fixed', endDateOffset: null },
                                              );
                                            } else if (newType === 'dynamic') {
                                              updateScheduleConfigMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                { endDateType: 'dynamic', fixedEndDate: null },
                                              );
                                            } else {
                                              // 'none' - clear both date values
                                              updateScheduleConfigMultipleFields(
                                                activeScheduleTab as RecurringFrequency,
                                                {
                                                  endDateType: 'none',
                                                  endDateOffset: null,
                                                  fixedEndDate: null,
                                                },
                                              );
                                            }
                                          }}
                                          className="h-7 pr-7 leading-7"
                                        >
                                          <option value="none">None</option>
                                          <option value="dynamic">Dynamic</option>
                                          <option value="fixed">Fixed</option>
                                        </FormSelect>
                                        {(frequencyScheduledRanges?.[
                                          activeScheduleTab as RecurringFrequency
                                        ]?.endDateType || 'none') === 'dynamic' && (
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <QuestionMarkCircleIcon className="absolute right-8 top-1/2 h-4 w-4 -translate-y-1/2 cursor-help text-ink-muted hover:text-ink-secondary" />
                                              </TooltipTrigger>
                                              <TooltipContent side="top">
                                                <p>
                                                  Offsets the End Date from when the Donor first
                                                  views the form
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        )}
                                      </div>

                                      {/* None or Dynamic: Offset selector (disabled when None, enabled when Dynamic) */}
                                      {((frequencyScheduledRanges?.[
                                        activeScheduleTab as RecurringFrequency
                                      ]?.endDateType || 'none') === 'dynamic' ||
                                        (frequencyScheduledRanges?.[
                                          activeScheduleTab as RecurringFrequency
                                        ]?.endDateType || 'none') === 'none') && (
                                        <FormSelect
                                          value={
                                            (frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.endDateType || 'none') === 'none'
                                              ? ''
                                              : (frequencyScheduledRanges?.[
                                                  activeScheduleTab as RecurringFrequency
                                                ]?.endDateOffset ?? '')
                                          }
                                          onChange={(e) =>
                                            updateScheduleConfigForFrequency(
                                              activeScheduleTab as RecurringFrequency,
                                              'endDateOffset',
                                              e.target.value ? parseInt(e.target.value, 10) : null,
                                            )
                                          }
                                          disabled={
                                            (frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.endDateType || 'none') === 'none'
                                          }
                                          className={`h-7 leading-7 ${
                                            (frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.endDateType || 'none') === 'none'
                                              ? 'bg-paper-inset text-ink-muted cursor-not-allowed'
                                              : ''
                                          } ${
                                            isFieldInvalid(
                                              `form_configuration-frequency-${activeScheduleTab}-endDate`,
                                            )
                                              ? 'border-red-500'
                                              : ''
                                          }`}
                                        >
                                          <option value="">-</option>
                                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                                            <option key={num} value={num}>
                                              In {num}{' '}
                                              {formatTimeUnit(
                                                activeScheduleTab as RecurringFrequency,
                                                num,
                                              )}
                                            </option>
                                          ))}
                                        </FormSelect>
                                      )}

                                      {/* Fixed: DatePicker */}
                                      {frequencyScheduledRanges?.[
                                        activeScheduleTab as RecurringFrequency
                                      ]?.endDateType === 'fixed' && (
                                        <SingleDatePicker
                                          value={
                                            frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.fixedEndDate
                                              ? dayjs(
                                                  frequencyScheduledRanges?.[
                                                    activeScheduleTab as RecurringFrequency
                                                  ]?.fixedEndDate,
                                                )
                                              : null
                                          }
                                          onChange={(date: dayjs.Dayjs | null) =>
                                            updateScheduleConfigForFrequency(
                                              activeScheduleTab as RecurringFrequency,
                                              'fixedEndDate',
                                              date ? date.format('YYYY-MM-DD') : null,
                                            )
                                          }
                                          disablePastDates={true}
                                          placeholder="Select date"
                                          isInvalid={
                                            isFieldInvalid(
                                              `form_configuration-frequency-${activeScheduleTab}-endDate`,
                                            ) ||
                                            (!!frequencyScheduledRanges?.[
                                              activeScheduleTab as RecurringFrequency
                                            ]?.fixedEndDate &&
                                              dayjs(
                                                frequencyScheduledRanges?.[
                                                  activeScheduleTab as RecurringFrequency
                                                ]?.fixedEndDate,
                                              ).isBefore(dayjs().startOf('day')))
                                          }
                                          className="h-7 w-full"
                                        />
                                      )}
                                    </div>
                                  </div>

                                  {/* Validation Error - only show after validation triggered */}
                                  {isFieldInvalid(
                                    `form_configuration-frequency-${activeScheduleTab}-scheduleOffsetValidation`,
                                  ) && (
                                    <p className="text-xs text-red-500">
                                      {getScheduleValidationMessage(
                                        activeScheduleTab as RecurringFrequency,
                                      )}
                                    </p>
                                  )}

                                  {/* Time (within the window section) */}
                                  <div>
                                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-secondary">
                                      Select Time
                                    </Label>
                                    <FormSelect
                                      value={
                                        frequencyScheduledRanges?.[
                                          activeScheduleTab as RecurringFrequency
                                        ]?.scheduledTime || ''
                                      }
                                      onChange={(e) =>
                                        updateScheduleConfigForFrequency(
                                          activeScheduleTab as RecurringFrequency,
                                          'scheduledTime',
                                          e.target.value,
                                        )
                                      }
                                      className="h-7 leading-7"
                                    >
                                      <option value="">Select time</option>
                                      {getTimeOptions().map((time) => (
                                        <option key={time} value={time}>
                                          {formatHourDisplay(time)}
                                        </option>
                                      ))}
                                    </FormSelect>
                                    {frequencyScheduledRanges?.[
                                      activeScheduleTab as RecurringFrequency
                                    ]?.scheduledTime ? (
                                      <p className="mt-1 text-xs text-ink-muted">
                                        Charged at this time in each donor&apos;s local time zone.
                                      </p>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* ── Section 2: Charge on a fixed day ── (not shown for Daily) */}
                            {activeScheduleTab !== 'Daily' && (
                              <div className="rounded-md border border-edge bg-white px-3 py-5">
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={isFixedChargeDayEnabled(
                                      activeScheduleTab as RecurringFrequency,
                                    )}
                                    onCheckedChange={(checked: boolean) =>
                                      toggleFixedChargeDay(
                                        activeScheduleTab as RecurringFrequency,
                                        checked,
                                      )
                                    }
                                  />
                                  <div>
                                    <div className="flex items-center gap-1">
                                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                                        Charge on a fixed day
                                      </Label>
                                      <TooltipProvider delayDuration={0}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <InformationCircleIcon className="h-4 w-4 cursor-help text-ink-muted hover:text-ink-secondary" />
                                          </TooltipTrigger>
                                          <TooltipContent side="top">
                                            <p>
                                              Have payments charge on a fixed day of the time
                                              period. On shorter months, days past the month-end
                                              fall on the last day (e.g. Feb 28).
                                            </p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                  </div>
                                </div>

                                {isFixedChargeDayEnabled(
                                  activeScheduleTab as RecurringFrequency,
                                ) && (
                                  <div className="mt-3 flex flex-wrap items-center gap-1.5 rounded-md border border-edge bg-paper-inset p-2 text-sm text-ink">
                                    <span>Charge on</span>

                                    {/* WEEKLY: day of week */}
                                    {activeScheduleTab === 'Weekly' && (
                                      <FormSelect
                                        value={String(getFixedChargeDay('Weekly') ?? '')}
                                        onChange={(e) =>
                                          updateScheduleConfigForFrequency(
                                            'Weekly',
                                            'fixedChargeDay',
                                            parseInt(e.target.value, 10),
                                          )
                                        }
                                        className="h-7 w-auto leading-7"
                                      >
                                        {WEEKDAY_LABELS.map((label, idx) => (
                                          <option key={label} value={idx}>
                                            {label}
                                          </option>
                                        ))}
                                      </FormSelect>
                                    )}

                                    {/* MONTHLY: day of month 1-31 ("the [day] of every month") */}
                                    {activeScheduleTab === 'Monthly' && (
                                      <>
                                        <span>the</span>
                                        <FormSelect
                                          value={String(getFixedChargeDay('Monthly') ?? '')}
                                          onChange={(e) =>
                                            updateScheduleConfigForFrequency(
                                              'Monthly',
                                              'fixedChargeDay',
                                              parseInt(e.target.value, 10),
                                            )
                                          }
                                          className="h-7 w-auto leading-2"
                                        >
                                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                                            (day) => (
                                              <option key={day} value={day}>
                                                {ordinalDayLabel(day)}
                                              </option>
                                            ),
                                          )}
                                        </FormSelect>
                                        <span>of every month</span>
                                      </>
                                    )}

                                    {/* ANNUALLY: month + day ("MM-DD") */}
                                    {activeScheduleTab === 'Annually' &&
                                      (() => {
                                        const [annualMonth, annualDay] = String(
                                          getFixedChargeDay('Annually') ?? '01-01',
                                        ).split('-');
                                        const monthNum = parseInt(annualMonth || '01', 10) || 1;
                                        const maxDay = daysInAnnualMonth(monthNum);
                                        return (
                                          <>
                                            <FormSelect
                                              value={annualMonth || '01'}
                                              onChange={(e) => {
                                                const newMonth = e.target.value;
                                                // Clamp the existing day to the new month's length so we
                                                // never persist an impossible date (e.g. switching to Feb
                                                // while day is 31 would yield "02-31").
                                                const currentDay =
                                                  parseInt(annualDay || '01', 10) || 1;
                                                const clampedDay = Math.min(
                                                  currentDay,
                                                  daysInAnnualMonth(parseInt(newMonth, 10) || 1),
                                                );
                                                updateScheduleConfigForFrequency(
                                                  'Annually',
                                                  'fixedChargeDay',
                                                  `${newMonth}-${String(clampedDay).padStart(2, '0')}`,
                                                );
                                              }}
                                              className="h-7 w-auto leading-7"
                                            >
                                              {MONTH_LABELS.map((label, idx) => (
                                                <option
                                                  key={label}
                                                  value={String(idx + 1).padStart(2, '0')}
                                                >
                                                  {label}
                                                </option>
                                              ))}
                                            </FormSelect>
                                            <FormSelect
                                              value={annualDay || '01'}
                                              onChange={(e) => {
                                                updateScheduleConfigForFrequency(
                                                  'Annually',
                                                  'fixedChargeDay',
                                                  `${annualMonth || '01'}-${e.target.value}`,
                                                );
                                              }}
                                              className="h-7 w-auto leading-7"
                                            >
                                              {Array.from({ length: maxDay }, (_, i) => i + 1).map(
                                                (day) => (
                                                  <option
                                                    key={day}
                                                    value={String(day).padStart(2, '0')}
                                                  >
                                                    {day}
                                                  </option>
                                                ),
                                              )}
                                            </FormSelect>
                                            <span>every year</span>
                                          </>
                                        );
                                      })()}

                                    {/* Time of day — shared scheduledTime value (mirrors
                                      the window section's Default Time). Applies to the
                                      fixed charge day. */}
                                    <span>at</span>
                                    <FormSelect
                                      value={
                                        frequencyScheduledRanges?.[
                                          activeScheduleTab as RecurringFrequency
                                        ]?.scheduledTime || ''
                                      }
                                      onChange={(e) =>
                                        updateScheduleConfigForFrequency(
                                          activeScheduleTab as RecurringFrequency,
                                          'scheduledTime',
                                          e.target.value,
                                        )
                                      }
                                      className="h-7 w-auto leading-7"
                                    >
                                      <option value="">time of payment</option>
                                      {getTimeOptions().map((time) => (
                                        <option key={time} value={time}>
                                          {formatHourDisplay(time)}
                                        </option>
                                      ))}
                                    </FormSelect>
                                    {/* Donor-local-time note — only relevant when a
                                      specific time is set (not "at time of payment"). */}
                                    {frequencyScheduledRanges?.[
                                      activeScheduleTab as RecurringFrequency
                                    ]?.scheduledTime ? (
                                      <p className="mt-1 w-full text-xs text-ink-muted">
                                        Charged at this time in each donor&apos;s local time zone.
                                      </p>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── Section 3: Lock schedule for donors ──
                              Only shown once a schedule is actually configured
                              (a fixed charge day, or a non-empty start/end window).
                              Defaults UNCHECKED: lockedScheduleDate is only true when
                              the org explicitly checks it. */}
                            {shouldShowLock(activeScheduleTab as RecurringFrequency) && (
                              <div className="flex items-center gap-1 px-1 pt-1">
                                <Checkbox
                                  checked={
                                    frequencyScheduledRanges?.[
                                      activeScheduleTab as RecurringFrequency
                                    ]?.lockedScheduleDate === true
                                  }
                                  onChange={(checked) => {
                                    updateScheduleConfigForFrequency(
                                      activeScheduleTab as RecurringFrequency,
                                      'lockedScheduleDate',
                                      checked,
                                    );
                                  }}
                                  label={`Lock ${activeScheduleTab} schedule for donors`}
                                />
                                <TooltipProvider delayDuration={0}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <InformationCircleIcon className="h-4 w-4 cursor-help text-ink-muted hover:text-ink-secondary" />
                                    </TooltipTrigger>
                                    <TooltipContent side="top">
                                      <p>
                                        Donors cannot edit or remove {activeScheduleTab} schedule
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: FORM_STEPS.AMOUNTS,
        title: 'Amounts',
        content: (
          <div>
            <div className="space-y-4">
              {/* Amount Button Type */}
              <div className="space-y-2">
                <Label>Amount Button</Label>
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-16">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="button-type"
                      value="standard"
                      className={`h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage ${
                        isFieldInvalid('form_configuration-amounts-formType')
                          ? 'border-red-500 border-2 text-red-500'
                          : 'border-ink-secondary text-sage'
                      }`}
                      checked={formType === 'EMBED_FORM'}
                      onChange={() => {
                        handleFieldFocus('form_configuration-amounts-formType');
                        handleFormTypeChange('EMBED_FORM');
                      }}
                    />
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Standard
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="button-type"
                      value="giving-levels"
                      className={`h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage ${
                        isFieldInvalid('form_configuration-amounts-formType')
                          ? 'border-red-500 border-2 text-red-500'
                          : 'border-ink-secondary text-sage'
                      }`}
                      checked={formType === 'CROWDFUNDING_FORM'}
                      onChange={() => {
                        handleFieldFocus('form_configuration-amounts-formType');
                        handleFormTypeChange('CROWDFUNDING_FORM');
                      }}
                    />
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Giving Levels
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="button-type"
                      value="sponsorships"
                      className={`h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage ${
                        isFieldInvalid('form_configuration-amounts-formType')
                          ? 'border-red-500 border-2 text-red-500'
                          : 'border-ink-secondary text-sage'
                      }`}
                      checked={formType === 'PRODUCT'}
                      onChange={() => {
                        handleFieldFocus('form_configuration-amounts-formType');
                        handleFormTypeChange('PRODUCT');
                      }}
                    />
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                      Sponsorships
                    </Label>
                  </div>
                </div>
              </div>

              {/* Amount Configuration - Standard */}
              {formType === 'EMBED_FORM' && (
                <div className="space-y-2 rounded-md border border-edge bg-paper-light p-2">
                  {/* Amount Tabs */}
                  {checkedValues.length > 1 && (
                    <nav
                      className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                      aria-label="Donation Amount Frequencies"
                    >
                      {checkedValues.map((freq, index) => {
                        // Check if ANY amount field for this frequency has an error (including defaultAmount)
                        const hasError = [
                          'amount1',
                          'amount2',
                          'amount3',
                          'amount4',
                          'defaultAmount',
                        ].some((amountField) =>
                          isFieldInvalid(getValidationFieldName(freq, index, amountField)),
                        );

                        return (
                          <button
                            key={`${freq}-${index}`}
                            type="button"
                            onClick={() => handleStandardAmountTabChange(freq, index)}
                            className={getFreqTabClasses(activeAmountTab === freq, hasError)}
                          >
                            {freq}
                          </button>
                        );
                      })}
                    </nav>
                  )}

                  {/* Tab Content */}
                  <div className="space-y-1 rounded-md border border-edge bg-paper-inset p-2">
                    {/* 2-up below sm so amount values stay legible at ~390px; 4-up from sm. */}
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      <div>
                        <Label
                          htmlFor="amount1"
                          className="block font-inter text-sm font-medium leading-5 text-ink-secondary"
                        >
                          Amount 1
                        </Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                            $
                          </span>
                          <FormInput
                            id="amount1"
                            type="text"
                            value={formatPriceWithCommas(getCurrentAmountValues().amount1)}
                            onChange={(e) => updateAmountForFrequency('amount1', e.target.value)}
                            onFocus={() =>
                              handleFieldFocus(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount1',
                                ),
                              )
                            }
                            className={`mt-0 h-7 pl-6 ${
                              isFieldInvalid(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount1',
                                ),
                              )
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                          />
                        </div>
                        {isFieldInvalid(
                          getValidationFieldName(activeAmountTab, activeAmountTabIndex, 'amount1'),
                        ) && <p className="mt-1 text-xs text-red-600">Must be at least $1</p>}
                      </div>
                      <div>
                        <Label
                          htmlFor="amount2"
                          className="block font-inter text-sm font-medium leading-5 text-ink-secondary"
                        >
                          Amount 2
                        </Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                            $
                          </span>
                          <FormInput
                            id="amount2"
                            type="text"
                            value={formatPriceWithCommas(getCurrentAmountValues().amount2)}
                            onChange={(e) => updateAmountForFrequency('amount2', e.target.value)}
                            onFocus={() =>
                              handleFieldFocus(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount2',
                                ),
                              )
                            }
                            className={`mt-0 h-7 pl-6 ${
                              isFieldInvalid(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount2',
                                ),
                              )
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                          />
                        </div>
                        {isFieldInvalid(
                          getValidationFieldName(activeAmountTab, activeAmountTabIndex, 'amount2'),
                        ) && <p className="mt-1 text-xs text-red-600">Must be at least $1</p>}
                      </div>
                      <div>
                        <Label
                          htmlFor="amount3"
                          className="block font-inter text-sm font-medium leading-5 text-ink-secondary"
                        >
                          Amount 3
                        </Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                            $
                          </span>
                          <FormInput
                            id="amount3"
                            type="text"
                            value={formatPriceWithCommas(getCurrentAmountValues().amount3)}
                            onChange={(e) => updateAmountForFrequency('amount3', e.target.value)}
                            onFocus={() =>
                              handleFieldFocus(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount3',
                                ),
                              )
                            }
                            className={`mt-0 h-7 pl-6 ${
                              isFieldInvalid(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount3',
                                ),
                              )
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                          />
                        </div>
                        {isFieldInvalid(
                          getValidationFieldName(activeAmountTab, activeAmountTabIndex, 'amount3'),
                        ) && <p className="mt-1 text-xs text-red-600">Must be at least $1</p>}
                      </div>
                      <div>
                        <Label
                          htmlFor="amount4"
                          className="block font-inter text-sm font-medium leading-5 text-ink-secondary"
                        >
                          Amount 4
                        </Label>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                            $
                          </span>
                          <FormInput
                            id="amount4"
                            type="text"
                            value={formatPriceWithCommas(getCurrentAmountValues().amount4)}
                            onChange={(e) => updateAmountForFrequency('amount4', e.target.value)}
                            onFocus={() =>
                              handleFieldFocus(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount4',
                                ),
                              )
                            }
                            className={`mt-0 h-7 pl-6 ${
                              isFieldInvalid(
                                getValidationFieldName(
                                  activeAmountTab,
                                  activeAmountTabIndex,
                                  'amount4',
                                ),
                              )
                                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                          />
                        </div>
                        {isFieldInvalid(
                          getValidationFieldName(activeAmountTab, activeAmountTabIndex, 'amount4'),
                        ) && <p className="mt-1 text-xs text-red-600">Must be at least $1</p>}
                      </div>
                    </div>

                    <div>
                      <Label
                        htmlFor="default-amount"
                        className="block font-inter text-sm font-medium leading-5 text-ink-secondary"
                      >
                        Default Suggested Amount (Optional)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                          $
                        </span>
                        <FormInput
                          id="default-amount"
                          type="text"
                          value={formatPriceWithCommas(getCurrentAmountValues().defaultAmount)}
                          onChange={(e) =>
                            updateAmountForFrequency('defaultAmount', e.target.value)
                          }
                          onFocus={() =>
                            handleFieldFocus(
                              getValidationFieldName(
                                activeAmountTab,
                                activeAmountTabIndex,
                                'defaultAmount',
                              ),
                            )
                          }
                          className={`mt-0 h-7 pl-6 ${
                            isFieldInvalid(
                              getValidationFieldName(
                                activeAmountTab,
                                activeAmountTabIndex,
                                'defaultAmount',
                              ),
                            )
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                              : ''
                          }`}
                        />
                      </div>
                      {isFieldInvalid(
                        getValidationFieldName(
                          activeAmountTab,
                          activeAmountTabIndex,
                          'defaultAmount',
                        ),
                      ) && <p className="mt-1 text-xs text-red-600">Must be at least $1</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* Amount Configuration - Giving Levels (NEW - with frequency tabs) */}
              {formType === 'CROWDFUNDING_FORM' && (
                <div className="space-y-2 rounded-md border border-edge bg-paper-light p-2">
                  {/* Frequency Tabs (mirrors Standard form) */}
                  {checkedValues.length > 1 && (
                    <nav
                      className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                      aria-label="Giving Level Frequencies"
                    >
                      {checkedValues.map((freq, index) => {
                        const freqOptions = getCrowdfundingOptionsForFrequency(freq);
                        // Check if ANY field for this frequency has an error
                        const hasError = freqOptions.some((_, optIndex) =>
                          ['title', 'amount', 'description'].some((field) =>
                            isFieldInvalid(
                              `form_configuration-amounts-crowdfunding-${freq}-${optIndex}-${field}`,
                            ),
                          ),
                        );

                        return (
                          <button
                            key={`${freq}-${index}`}
                            type="button"
                            onClick={() => handleCrowdfundingTabChange(freq)}
                            className={getFreqTabClasses(activeCrowdfundingTab === freq, hasError)}
                          >
                            {freq}
                          </button>
                        );
                      })}
                    </nav>
                  )}

                  {/* Giving Levels for Current Frequency */}
                  <div className="flex flex-col gap-2">
                    {getCrowdfundingOptionsForFrequency(activeCrowdfundingTab).map(
                      (level, index) => (
                        <div key={level.id}>
                          <Label className="text-base font-medium text-ink-secondary">
                            Giving Level {index + 1}
                          </Label>
                          <div className="relative rounded-md border border-edge bg-paper-inset p-2">
                            <div className="absolute top-1 right-1">
                              {getCrowdfundingOptionsForFrequency(activeCrowdfundingTab).length >
                                1 && (
                                <button
                                  onClick={() => {
                                    // Remove from ALL frequencies (structural consistency requirement)
                                    removeCrowdfundingOptionAcrossAllFrequencies(index);
                                  }}
                                  className="hover:opacity-70"
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                      d="M4 4L12 12M4 12L12 4"
                                      stroke="#3A3429"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>

                            <div className="space-y-2">
                              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-2">
                                {/* PER-FREQUENCY FIELD - Title specific to this frequency */}
                                <div className="flex-1 w-full sm:w-auto">
                                  <Label
                                    htmlFor={`level-title-${activeCrowdfundingTab}-${index}`}
                                    className="text-sm text-ink-secondary"
                                  >
                                    Title
                                  </Label>
                                  <FormInput
                                    id={`level-title-${activeCrowdfundingTab}-${index}`}
                                    type="text"
                                    value={level.title || ''}
                                    onChange={(e) =>
                                      updateTitleForFrequency(
                                        activeCrowdfundingTab,
                                        index,
                                        e.target.value,
                                      )
                                    }
                                    onFocus={() =>
                                      handleFieldFocus(
                                        `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-title`,
                                      )
                                    }
                                    className={`h-7 ${
                                      isFieldInvalid(
                                        `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-title`,
                                      )
                                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                        : ''
                                    }`}
                                  />
                                </div>

                                {/* PER-FREQUENCY FIELD - Amount specific to this frequency */}
                                <div className="flex-1 w-full sm:w-auto">
                                  <Label
                                    htmlFor={`level-amount-${activeCrowdfundingTab}-${index}`}
                                    className="text-sm text-ink-secondary"
                                  >
                                    Amount
                                  </Label>
                                  <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                                      $
                                    </span>
                                    <FormInput
                                      id={`level-amount-${activeCrowdfundingTab}-${index}`}
                                      type="text"
                                      value={formatPriceWithCommas(level.amount || '')}
                                      onChange={(e) =>
                                        updateCrowdfundingOptionAmount(
                                          activeCrowdfundingTab,
                                          index,
                                          e.target.value,
                                        )
                                      }
                                      onFocus={() =>
                                        handleFieldFocus(
                                          `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-amount`,
                                        )
                                      }
                                      className={`h-7 pl-6 ${
                                        isFieldInvalid(
                                          `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-amount`,
                                        )
                                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                          : ''
                                      }`}
                                    />
                                  </div>
                                  {isFieldInvalid(
                                    `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-amount`,
                                  ) && (
                                    <p className="mt-1 text-xs text-red-600">Must be at least $1</p>
                                  )}
                                </div>
                              </div>

                              {/* PER-FREQUENCY FIELD - Description specific to this frequency */}
                              <div>
                                <Label
                                  htmlFor={`level-description-${activeCrowdfundingTab}-${index}`}
                                  className="text-sm text-ink-secondary"
                                >
                                  Amount Description
                                </Label>
                                <FormTextarea
                                  id={`level-description-${activeCrowdfundingTab}-${index}`}
                                  value={level.description || ''}
                                  onChange={(e) =>
                                    updateDescriptionForFrequency(
                                      activeCrowdfundingTab,
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  onFocus={() =>
                                    handleFieldFocus(
                                      `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-description`,
                                    )
                                  }
                                  className={`resize-none ${
                                    isFieldInvalid(
                                      `form_configuration-amounts-crowdfunding-${activeCrowdfundingTab}-${index}-description`,
                                    )
                                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                      : ''
                                  }`}
                                  rows={1}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ),
                    )}

                    <div className="flex justify-end">
                      {givingLevelsLimitReached ? (
                        <div className="text-sm text-red-500">
                          You can only create up to four giving levels.
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            // Add to ALL frequencies (structural consistency requirement)
                            addCrowdfundingOptionAcrossAllFrequencies();
                          }}
                          className="flex items-center gap-1 pt-3 text-sm text-sage hover:text-sage-hover"
                        >
                          <PlusIcon className="h-5 w-5" />
                          <span>Add Giving Level</span>
                        </button>
                      )}
                    </div>
                    <div>
                      <Label
                        htmlFor="default-amount-crowdfunding"
                        className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                      >
                        Default Suggested Amount (Optional)
                      </Label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                          $
                        </span>
                        <FormInput
                          id="default-amount-crowdfunding"
                          type="text"
                          value={formatPriceWithCommas(
                            frequencyAmounts?.[activeCrowdfundingTab as keyof FrequencyAmountsMap]
                              ?.defaultAmount || '',
                          )}
                          onChange={(e) =>
                            updateAmountForFrequency('defaultAmount', e.target.value)
                          }
                          onFocus={() =>
                            handleFieldFocus(
                              'form_configuration-amounts-crowdfunding-defaultAmount',
                            )
                          }
                          className={`mt-0 h-7 pl-6 ${
                            isFieldInvalid('form_configuration-amounts-crowdfunding-defaultAmount')
                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                              : ''
                          }`}
                        />
                      </div>
                      {isFieldInvalid('form_configuration-amounts-crowdfunding-defaultAmount') && (
                        <p className="mt-1 text-xs text-red-600">Must be at least $1</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Amount Configuration - Sponsorships (Product Groups) */}
              {formType === 'PRODUCT' && (
                <div className="space-y-2 rounded-md border border-edge bg-paper-light p-2">
                  {/* Frequency Tabs (NEW - mirrors Standard form + Giving Levels pattern) */}
                  {checkedValues.length > 1 && (
                    <nav className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2">
                      {checkedValues.map((freq, index) => {
                        const freqGroups = getProductGroupsForFrequency(freq);
                        const hasError = freqGroups.some((group, groupIndex) => {
                          const groupNameError = isFieldInvalid(
                            `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-name`,
                          );
                          const productErrors = group.products.some((_, productIndex) =>
                            ['name', 'price'].some((field) =>
                              isFieldInvalid(
                                `form_configuration-amounts-sponsorship-${freq}-${groupIndex}-product-${productIndex}-${field}`,
                              ),
                            ),
                          );
                          return groupNameError || productErrors;
                        });

                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => handleSponsorshipTabChange(freq)}
                            className={getFreqTabClasses(activeSponsorshipTab === freq, hasError)}
                          >
                            {freq}
                          </button>
                        );
                      })}
                    </nav>
                  )}

                  {/* Product Groups for Current Frequency */}
                  {getProductGroupsForFrequency(activeSponsorshipTab).map((group, groupIndex) => (
                    <div key={group.id}>
                      <Label className="text-base font-medium text-ink-secondary">
                        Sponsorship Group {groupIndex + 1}
                      </Label>

                      <div className="space-y-1 rounded-md border border-edge bg-paper-inset p-2">
                        {/* Group Title and Description */}
                        <div className="space-y-2">
                          <div>
                            <div className="flex items-center pt-0.5 justify-between">
                              <Label
                                htmlFor={`group-title-${groupIndex}`}
                                className="text-sm text-ink-secondary"
                              >
                                Group Title
                              </Label>
                              {getProductGroupsForFrequency(activeSponsorshipTab).length > 1 && (
                                <button
                                  onClick={() => {
                                    // Remove from ALL frequencies (structural consistency requirement)
                                    removeProductGroupAcrossAllFrequencies(groupIndex);
                                  }}
                                  className="hover:opacity-70"
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                      d="M4 4L12 12M4 12L12 4"
                                      stroke="#3A3429"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                            <FormInput
                              id={`group-title-${groupIndex}`}
                              type="text"
                              value={group.productGroupName}
                              onChange={(e) =>
                                updateProductGroupNameForFrequency(
                                  activeSponsorshipTab,
                                  groupIndex,
                                  e.target.value,
                                )
                              }
                              onFocus={() =>
                                handleFieldFocus(
                                  `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-name`,
                                )
                              }
                              className={`h-7 ${
                                isFieldInvalid(
                                  `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-name`,
                                )
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                  : ''
                              }`}
                            />
                          </div>

                          <div>
                            <Label
                              htmlFor={`group-description-${groupIndex}`}
                              className="text-sm text-ink-secondary"
                            >
                              Description
                            </Label>
                            <FormInput
                              id={`group-description-${groupIndex}`}
                              type="text"
                              value={group.productGroupDescription}
                              onChange={(e) =>
                                updateProductGroupDescriptionForFrequency(
                                  activeSponsorshipTab,
                                  groupIndex,
                                  e.target.value,
                                )
                              }
                              className="h-7"
                            />
                          </div>
                        </div>

                        {/* Product Options Tabs */}
                        <div className="space-y-2 mt-4">
                          {/* Tabs */}
                          <div
                            className="flex gap-2 pt-3 
                          border-b border-edge overflow-x-auto scrollbar-hide"
                          >
                            {group.products.map((product, productIndex) => (
                              <div
                                className="relative min-w-20 flex-shrink-0"
                                key={product.productID}
                              >
                                <button
                                  type="button"
                                  onClick={() =>
                                    setActiveProductTabs({
                                      ...activeProductTabs,
                                      [groupIndex]: productIndex,
                                    })
                                  }
                                  className={`relative ${getFreqTabClasses((activeProductTabs[groupIndex] ?? 0) === productIndex, false)}`}
                                >
                                  Item {productIndex + 1}
                                </button>
                                {group.products.length > 1 && (
                                  <div
                                    className={`absolute top-0 ${
                                      productIndex > 8 ? '-right-[4px]' : 'right-0'
                                    }`}
                                  >
                                    <button
                                      onClick={() =>
                                        removeProductAcrossAllFrequencies(groupIndex, productIndex)
                                      }
                                      className="hover:opacity-70"
                                    >
                                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                        <path
                                          d="M4 4L12 12M4 12L12 4"
                                          stroke="#3A3429"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => addProductAcrossAllFrequencies(groupIndex)}
                              className="py-1 text-sm text-sage hover:text-sage-hover whitespace-nowrap flex-shrink-0"
                            >
                              <PlusIcon className="inline h-4 w-4" />
                              <span>Add Item</span>
                            </button>
                          </div>

                          {/* Tab Content */}
                          {group.products.map((product, productIndex) => (
                            <div
                              key={product.productID}
                              className={
                                (activeProductTabs[groupIndex] ?? 0) === productIndex
                                  ? 'block'
                                  : 'hidden'
                              }
                            >
                              <div className="space-y-2 pb-2">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1 flex items-start gap-2">
                                    <div className="flex-1">
                                      <Label
                                        htmlFor={`product-name-${groupIndex}-${productIndex}`}
                                        className="text-sm text-ink-secondary"
                                      >
                                        Item Title
                                      </Label>
                                      <FormInput
                                        id={`product-name-${groupIndex}-${productIndex}`}
                                        type="text"
                                        value={product.productName}
                                        onChange={(e) =>
                                          updateProductNameForFrequency(
                                            activeSponsorshipTab,
                                            groupIndex,
                                            productIndex,
                                            e.target.value,
                                          )
                                        }
                                        onFocus={() =>
                                          handleFieldFocus(
                                            `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-product-${productIndex}-name`,
                                          )
                                        }
                                        className={`h-7 ${
                                          isFieldInvalid(
                                            `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-product-${productIndex}-name`,
                                          )
                                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                            : ''
                                        }`}
                                      />
                                      {/* Reserve space for error message to maintain alignment */}
                                      <div className="h-5 mt-1">
                                        {/* Error placeholder - keeps height consistent with Amount field */}
                                      </div>
                                    </div>

                                    <div className="flex-1">
                                      <Label
                                        htmlFor={`product-price-${groupIndex}-${productIndex}`}
                                        className="text-sm text-ink-secondary"
                                      >
                                        Amount
                                      </Label>
                                      <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-ink-muted pointer-events-none">
                                          $
                                        </span>
                                        <FormInput
                                          id={`product-price-${groupIndex}-${productIndex}`}
                                          type="text"
                                          value={formatPriceWithCommas(product.productPrice)}
                                          onChange={(e) =>
                                            updateProductPriceForFrequency(
                                              activeSponsorshipTab,
                                              groupIndex,
                                              productIndex,
                                              e.target.value,
                                            )
                                          }
                                          onFocus={() =>
                                            handleFieldFocus(
                                              `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-product-${productIndex}-price`,
                                            )
                                          }
                                          className={`h-7 pl-6 ${
                                            isFieldInvalid(
                                              `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-product-${productIndex}-price`,
                                            )
                                              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                                              : ''
                                          }`}
                                        />
                                      </div>
                                      <div className="h-5 mt-1">
                                        {isFieldInvalid(
                                          `form_configuration-amounts-sponsorship-${activeSponsorshipTab}-${groupIndex}-product-${productIndex}-price`,
                                        ) && (
                                          <p className="text-xs text-red-600">
                                            Must be at least $1
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-end">
                    {sponsorshipGroupsLimitReached ? (
                      <div className="text-sm text-red-500">
                        You can only create up to four sponsorship groups.
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          // Add to ALL frequencies (structural consistency requirement)
                          addProductGroupAcrossAllFrequencies();
                        }}
                        className="flex items-center gap-1 pt-3 text-sm text-sage hover:text-sage-hover"
                      >
                        <PlusIcon className="h-5 w-5" />
                        <span>Add Sponsorship Group</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
      },
      {
        key: FORM_STEPS.CHECKOUT,
        title: 'Checkout Details',
        content: (
          <div className="space-y-5">
            {/* Donor Information */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-ink-secondary">Donor Information</Label>
              <div className="space-y-3">
                {/* Enable Anonymous Name */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={showAnonymousDonations}
                    onCheckedChange={setShowAnonymousDonations}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Enable Anonymous Name
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Let donors hide their name from public-facing displays.
                    </p>
                  </div>
                </div>

                {/* Enable Organization Name */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={showOrganizationNameField}
                    onCheckedChange={setShowOrganizationNameField}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Enable Organization Name
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Collect an optional company or organization name.
                    </p>
                  </div>
                </div>

                {/* Enable Phone Number */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch checked={enablePhoneNumber} onCheckedChange={setEnablePhoneNumber} />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Enable Phone Number
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Ask donors for a phone number at checkout.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Payment Settings */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-ink-secondary">Payment Settings</Label>
              <div className="space-y-3">
                {/* Enable Billing Address */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableBillingAddress}
                      onCheckedChange={setEnableBillingAddress}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Billing Address
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Collect a billing address during checkout.
                      </p>
                    </div>
                  </div>

                  {enableBillingAddress && (
                    <div className="space-y-2 rounded-md border border-edge bg-paper-light p-2">
                      <div className="space-y-1">
                        <Label
                          htmlFor="default-country"
                          className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                        >
                          Default Country
                        </Label>
                        <FormSelect
                          id="default-country"
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                          className="h-7 leading-7"
                        >
                          <option value="">Select a country</option>
                          {selectedCountry && !countryLabels.has(selectedCountry) && (
                            <option value={selectedCountry}>{selectedCountry}</option>
                          )}
                          {countries.map((country: { value: string; label: string }) => (
                            <option key={country.value} value={country.label}>
                              {country.label}
                            </option>
                          ))}
                        </FormSelect>
                      </div>
                    </div>
                  )}
                </div>

                {/* Always Cover Fees */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={lockCoverFees}
                    onCheckedChange={(value) => setLockCoverFees?.(value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Always Cover Fees
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Donors can&rsquo;t opt out of covering processing fees.
                    </p>
                  </div>
                </div>

                {/* Disable Checkout Bump */}
                <div className="flex items-center gap-2 pl-1">
                  <Switch
                    checked={disableCheckoutBump}
                    onCheckedChange={(value) => setDisableCheckoutBump?.(value)}
                  />
                  <div>
                    <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                      Disable Checkout Bump
                    </Label>
                    <p className="text-xs text-ink-muted">
                      Hides the &ldquo;increase your impact&rdquo; upsell at checkout for this form.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Additional Features */}
            <div className="space-y-3">
              <Label className="text-base font-medium text-ink-secondary">
                Additional Features
              </Label>
              <div className="space-y-3">
                {/* Enable Communication Consent */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableCommunicationConsent}
                      onCheckedChange={setEnableCommunicationConsent}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Communication Consent
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Show a marketing communication opt-in checkbox.
                      </p>
                    </div>
                  </div>

                  {enableCommunicationConsent && (
                    <div className="space-y-2 rounded-md border border-edge bg-paper-light p-2">
                      <div className="space-y-1">
                        <Label
                          htmlFor="communication-consent-message"
                          className="block font-inter text-sm font-medium leading-5 text-ink-muted"
                        >
                          Custom Message (Optional)
                        </Label>
                        <FormTextarea
                          id="communication-consent-message"
                          value={communicationConsentMessage}
                          onChange={(e) => setCommunicationConsentMessage(e.target.value)}
                          className="min-h-[60px] text-sm"
                          maxLength={500}
                        />
                        <p className="text-xs text-ink-muted text-right">
                          {communicationConsentMessage.length}/500 characters
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Enable Dedication */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 pl-1">
                    <Switch checked={enableDedication} onCheckedChange={setEnableDedication} />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Dedication Information
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Let donors dedicate their gift in honor or memory of someone.
                      </p>
                    </div>
                  </div>

                  {enableDedication && (
                    <div className="space-y-3 rounded-md border border-edge bg-paper-inset p-2">
                      {/* Dedication Types */}
                      <div className="space-y-1">
                        <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                          Dedication Types
                          <span className="text-red-500">&nbsp;*</span>
                        </Label>
                        <MultiSelectDropdownNew
                          height={28}
                          backgroundColor="#FFFFFF"
                          options={[
                            { value: 'In Honor Of', label: 'In Honor Of' },
                            { value: 'In Memory Of', label: 'In Memory Of' },
                          ]}
                          items={dedicationTypes}
                          setItems={(items) => {
                            setDedicationTypes(items);
                            if (items.length > 0) {
                              handleFieldFocus('form_configuration-checkout-dedicationTypes');
                            }
                          }}
                          error={isFieldInvalid('form_configuration-checkout-dedicationTypes')}
                        />
                      </div>

                      {/* Enable Dedication Message */}
                      <div className="flex items-center gap-2 pl-1">
                        <Switch
                          checked={enableDedicationMessage}
                          onCheckedChange={setEnableDedicationMessage}
                        />
                        <div>
                          <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                            Enable Dedication Message
                          </Label>
                          <p className="text-xs text-ink-muted">
                            Allow donors to add a personal dedication message.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Enable Double the Donation */}
                {dtdPublicApiKey !== null && (
                  <div className="flex items-center gap-2 pl-1">
                    <Switch
                      checked={enableDoubleTheDonation}
                      onCheckedChange={setEnableDoubleTheDonation}
                    />
                    <div>
                      <Label className="block font-inter text-sm font-medium leading-5 text-ink">
                        Enable Double the Donation
                      </Label>
                      <p className="text-xs text-ink-muted">
                        Show employer gift-matching search at checkout.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
    ];

    return (
      <div className="space-y-4">
        {steps.map((step) => {
          const isCurrentStep = step.key === currentStep;
          const hasErrors = getStepValidationErrors(step.key);
          const workspaceStepPresentation = {
            [FORM_STEPS.APPEARANCE]: {
              icon: PaintBrushIcon,
              description: 'Set the form identity, visual treatment, and supporter-facing content.',
            },
            [FORM_STEPS.FREQUENCY]: {
              icon: ArrowPathIcon,
              description: 'Choose which giving cadences are available and how they are scheduled.',
            },
            [FORM_STEPS.AMOUNTS]: {
              icon: BanknotesIcon,
              description: 'Configure suggested gifts, giving levels, and sponsorship options.',
            },
            [FORM_STEPS.CHECKOUT]: {
              icon: CreditCardIcon,
              description: 'Control the supporter information and options collected at checkout.',
            },
          }[step.key];

          const shouldRenderStepContent = workspacePresentation || isCurrentStep;
          const stepContent = shouldRenderStepContent ? (
            <div className="space-y-4">
              {step.content}

              {!workspacePresentation && (
                <div className="mt-6">
                  <ButtonGroup
                    currentStep={currentStep}
                    onBack={handleBack}
                    onContinue={handleContinue}
                    isLastStep={isLastStep()}
                    validateCurrentStep={validateCurrentStep}
                  />
                </div>
              )}
            </div>
          ) : null;

          if (workspacePresentation) {
            return (
              <div key={step.key} className="scroll-mt-4">
                <WorkspaceSectionCard
                  title={step.title}
                  description={workspaceStepPresentation?.description}
                  icon={workspaceStepPresentation?.icon}
                  expanded
                  hasErrors={hasErrors}
                >
                  {stepContent}
                </WorkspaceSectionCard>
              </div>
            );
          }

          return (
            <div key={step.key} className="space-y-4">
              {/* Section Header — whole section (header + content) is Driver.js target */}
              <SectionHeader
                title={step.title}
                onClick={() => setCurrentStep(step.key as typeof currentStep)}
                hasErrors={hasErrors}
                className="mb-0"
              />

              {/* Step Content - Only show if current step */}
              {stepContent}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`w-full space-y-2 ${workspacePresentation ? '' : 'pr-2'}`}>
      {renderAllFormSteps()}
    </div>
  );
}

// Export types for use in parent component
export type { CrowdfundingOption, ProductOption, ProductGroup };
